import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { moveListItemByOffset } from './blockOrdering';
import QRCode from 'qrcode';
import { ChevronLeft, ChevronRight, Eye, Flag, ImageIcon, MapIcon, Megaphone, PanelTop, Search } from 'lucide-react';
import PhotoAssetBrowser from '../PhotoAssetBrowser';
import SelectedAssetPreview from '../SelectedAssetPreview';
import { isImageAsset } from '../assetUtils';
import { parsePlaqueContent, serializePlaqueContent } from './plaqueContent';
import { getSubscriptionTypeFromToken, getUserRoleFromToken } from '../../../utils/jwt';

const LANGUAGE_OPTIONS = [
  { code: 'vi', label: 'Vietnamese' },
  { code: 'ko', label: 'Korean' },
  { code: 'ru', label: 'Russian' },
  { code: 'zh', label: 'Chinese (Simplified)' },
  { code: 'zh-TW', label: 'Chinese (Traditional)' },
  { code: 'hi', label: 'Hindi' },
  { code: 'ja', label: 'Japanese' },
  { code: 'fr', label: 'French' },
  { code: 'de', label: 'German' },
  { code: 'es', label: 'Spanish' },
  { code: 'th', label: 'Thai' },
  { code: 'id', label: 'Indonesian' },
  { code: 'ms', label: 'Malay' },
  { code: 'en', label: 'English' },
];

const MONETISATION_MODE_OPTIONS = [
  { value: 'off', label: 'Off', description: 'No auto-inserted promotions.' },
  { value: 'subtle', label: 'Subtle', description: 'Inline links only.' },
  { value: 'standard', label: 'Standard', description: 'Inline links and activity blocks.' },
  { value: 'full', label: 'Full', description: 'Inline links, activity blocks, and city widgets.' },
];

const LANGUAGE_OPTION_MAP = new Map(
  LANGUAGE_OPTIONS.map((option) => [String(option.code || '').trim().toLowerCase(), option])
);

const normalizeLanguageCode = (value, fallback = '') => {
  const code = String(value || '').trim();
  return code || fallback;
};

const normalizeMonetisationMode = (value, fallback = 'off') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (MONETISATION_MODE_OPTIONS.some((option) => option.value === normalized)) return normalized;
  return fallback;
};

const getMonetisationModeLabel = (value) => (
  MONETISATION_MODE_OPTIONS.find((option) => option.value === normalizeMonetisationMode(value, 'off'))?.label || 'Off'
);

const normalizeReviewStatus = (value, fallback = 'draft') => {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'approved' || normalized === 'reviewed' || normalized === 'draft') {
    return normalized;
  }
  return fallback;
};

const sameLanguageCode = (a, b) => {
  const left = normalizeLanguageCode(a).toLowerCase();
  const right = normalizeLanguageCode(b).toLowerCase();
  return Boolean(left) && left === right;
};

const getLanguageLabel = (code) => {
  const normalized = normalizeLanguageCode(code);
  if (!normalized) return 'Unknown';
  return LANGUAGE_OPTION_MAP.get(normalized.toLowerCase())?.label || normalized.toUpperCase();
};

const normalizeTranslations = (translations) => {
  const rows = Array.isArray(translations) ? translations : [];
  return rows
    .map((row) => ({
      languageCode: row.language_code || row.languageCode || '',
      title: row.title || '',
      translatedText: row.translated_text || row.translatedText || '',
      autoGenerated: Boolean(row.auto_generated || row.autoGenerated),
      reviewStatus: normalizeReviewStatus(row.review_status || row.reviewStatus, 'draft'),
      mapYouAreHereLabel: row.map_you_are_here_label || row.mapYouAreHereLabel || '',
    }))
    .filter((row) => row.languageCode);
};

const sortLanguageVersions = (rows, sourceLanguageCode) => (
  [...rows].sort((a, b) => {
    if (sameLanguageCode(a.languageCode, sourceLanguageCode)) return -1;
    if (sameLanguageCode(b.languageCode, sourceLanguageCode)) return 1;
    return String(a.languageCode || '').localeCompare(String(b.languageCode || ''));
  })
);

const normalizeLanguageVersions = ({ plaque, translations, languageVersions }) => {
  const normalizedSourceLanguageCode = normalizeLanguageCode(
    plaque?.sourceLanguageCode || plaque?.source_language_code,
    'en'
  );
  const sourceTitle = plaque?.title || '';
  const sourceBodyText = plaque?.english_text || plaque?.englishText || '';
  const sourceMapLabel = plaque?.map_you_are_here_override || plaque?.mapYouAreHereOverride || '';

  const normalizedRows = Array.isArray(languageVersions) && languageVersions.length
    ? languageVersions
        .map((row) => {
          const languageCode = normalizeLanguageCode(row.languageCode || row.language_code);
          if (!languageCode) return null;
          const bodyText = typeof row.bodyText === 'string'
            ? row.bodyText
            : typeof row.translatedText === 'string'
              ? row.translatedText
              : typeof row.translated_text === 'string'
                ? row.translated_text
                : '';
          return {
            languageCode,
            isSource: Boolean(row.isSource) || sameLanguageCode(languageCode, normalizedSourceLanguageCode),
            title: row.title || '',
            bodyText,
            translatedText: bodyText,
            mapYouAreHereLabel: row.mapYouAreHereLabel || row.map_you_are_here_label || '',
            autoGenerated: Boolean(row.autoGenerated || row.auto_generated),
            reviewStatus: normalizeReviewStatus(row.reviewStatus || row.review_status, 'draft'),
            freshness: row.freshness || (sameLanguageCode(languageCode, normalizedSourceLanguageCode) ? 'source' : 'current'),
            isOutOfDate: Boolean(row.isOutOfDate),
            sourceContentUpdatedAt: row.sourceContentUpdatedAt || row.source_content_updated_at || null,
            contentUpdatedAt: row.contentUpdatedAt || row.updatedAt || row.updated_at || row.createdAt || row.created_at || null,
          };
        })
        .filter(Boolean)
    : normalizeTranslations(translations).map((row) => ({
        languageCode: row.languageCode,
        isSource: false,
        title: row.title || '',
        bodyText: row.translatedText || '',
        translatedText: row.translatedText || '',
        mapYouAreHereLabel: row.mapYouAreHereLabel || '',
        autoGenerated: Boolean(row.autoGenerated),
        reviewStatus: normalizeReviewStatus(row.reviewStatus, 'draft'),
        freshness: 'current',
        isOutOfDate: false,
        sourceContentUpdatedAt: null,
        contentUpdatedAt: null,
      }));

  const byLanguage = new Map();
  normalizedRows.forEach((row) => {
    byLanguage.set(row.languageCode, row);
  });

  const existingSource = byLanguage.get(normalizedSourceLanguageCode) || null;
  byLanguage.set(normalizedSourceLanguageCode, {
    languageCode: normalizedSourceLanguageCode,
    isSource: true,
    title: sourceTitle || existingSource?.title || '',
    bodyText: sourceBodyText || existingSource?.bodyText || existingSource?.translatedText || '',
    translatedText: sourceBodyText || existingSource?.bodyText || existingSource?.translatedText || '',
    mapYouAreHereLabel: sourceMapLabel || existingSource?.mapYouAreHereLabel || '',
    autoGenerated: false,
    reviewStatus: 'draft',
    freshness: 'source',
    isOutOfDate: false,
    sourceContentUpdatedAt: plaque?.sourceContentUpdatedAt || plaque?.source_content_updated_at || existingSource?.sourceContentUpdatedAt || null,
    contentUpdatedAt: plaque?.sourceContentUpdatedAt || plaque?.source_content_updated_at || plaque?.updated_at || plaque?.updatedAt || existingSource?.contentUpdatedAt || null,
  });

  return sortLanguageVersions(Array.from(byLanguage.values()), normalizedSourceLanguageCode).map((row) => ({
    ...row,
    isSource: sameLanguageCode(row.languageCode, normalizedSourceLanguageCode),
    freshness: sameLanguageCode(row.languageCode, normalizedSourceLanguageCode) ? 'source' : (row.freshness || 'current'),
    isOutOfDate: sameLanguageCode(row.languageCode, normalizedSourceLanguageCode) ? false : Boolean(row.isOutOfDate),
  }));
};

const getDefaultCompareToLanguageCode = (rows, sourceLanguageCode) => {
  const safeRows = Array.isArray(rows) ? rows : [];
  const firstNonSource = safeRows.find((row) => !sameLanguageCode(row.languageCode, sourceLanguageCode));
  if (firstNonSource?.languageCode) return firstNonSource.languageCode;
  if (!sameLanguageCode(sourceLanguageCode, 'en')) return 'en';
  return LANGUAGE_OPTIONS.find((option) => !sameLanguageCode(option.code, sourceLanguageCode))?.code || sourceLanguageCode;
};

const getFreshnessBadge = (row) => {
  if (!row) return { label: 'Missing', classes: 'bg-gray-800 text-gray-200' };
  if (row.isSource) return { label: 'Source', classes: 'bg-indigo-900/70 text-indigo-100' };
  if (row.isOutOfDate || row.freshness === 'stale') return { label: 'Out of date', classes: 'bg-amber-900/70 text-amber-100' };
  if (normalizeReviewStatus(row.reviewStatus, 'draft') === 'approved') return { label: 'Approved', classes: 'bg-emerald-900/70 text-emerald-100' };
  if (normalizeReviewStatus(row.reviewStatus, 'draft') === 'reviewed') return { label: 'Reviewed', classes: 'bg-lime-900/70 text-lime-100' };
  if (row.autoGenerated) return { label: 'AI-generated', classes: 'bg-cyan-900/70 text-cyan-100' };
  return { label: 'Draft', classes: 'bg-emerald-900/70 text-emerald-100' };
};

const getOriginBadge = (row) => {
  if (!row || row.isSource) return null;
  if (row.autoGenerated) return { label: 'AI', classes: 'bg-cyan-950/80 text-cyan-200 border border-cyan-800/70' };
  return { label: 'Manual', classes: 'bg-gray-900 text-gray-200 border border-gray-700' };
};

function getRevisionKindLabel(kind) {
  const normalized = String(kind || 'manual').trim().toLowerCase() || 'manual';
  if (normalized === 'kryten') return 'Kryten';
  if (normalized === 'nina') return 'Nina';
  return 'Manual';
}

const BLOCK_CARD_CLASSES = {
  text: 'border-l-4 border-l-sky-300 border-sky-300/70 bg-sky-950/80 shadow-[0_0_0_1px_rgba(125,211,252,0.18)]',
  image: 'border-l-4 border-l-fuchsia-300 border-fuchsia-300/70 bg-fuchsia-950/70 shadow-[0_0_0_1px_rgba(240,171,252,0.18)]',
  subtitle: 'border-indigo-900/60 bg-indigo-950/35',
  section: 'border-l-4 border-l-amber-300 border-amber-300/75 bg-amber-950/75 shadow-[0_0_0_1px_rgba(252,211,77,0.18)]',
  ad: 'border-emerald-900/55 bg-emerald-950/30',
  campaign: 'border-rose-900/55 bg-rose-950/25',
  campaign_dynamic: 'border-rose-800/60 bg-rose-950/35 shadow-[0_0_0_1px_rgba(251,113,133,0.15)]',
  widget: 'border-cyan-900/55 bg-cyan-950/30',
  default: 'border-gray-800 bg-gray-950',
};

const BLOCK_LABEL_CLASSES = {
  text: 'text-sky-200',
  image: 'text-fuchsia-200',
  subtitle: 'text-indigo-200',
  section: 'text-amber-200',
  ad: 'text-emerald-200',
  campaign: 'text-rose-200',
  campaign_dynamic: 'text-rose-100',
  widget: 'text-cyan-200',
  default: 'text-gray-400',
};

const BLOCK_FIELD_CLASSES = {
  text: 'bg-sky-950/90 border-sky-300/35 focus:border-sky-200',
  image: 'bg-fuchsia-950/85 border-fuchsia-300/35 focus:border-fuchsia-200',
  section: 'bg-amber-950/85 border-amber-300/40 focus:border-amber-200',
  campaign: 'bg-rose-950/70 border-rose-300/25 focus:border-rose-200',
  campaign_dynamic: 'bg-rose-950/80 border-rose-300/30 focus:border-rose-200',
  default: 'bg-gray-900 border-gray-800 focus:border-gray-600',
};

let nextBlockId = 1;

function createBlockId() {
  const id = nextBlockId;
  nextBlockId += 1;
  return `plaque-block-${id}`;
}

function normalizeBlocks(blocks) {
  const safeBlocks = Array.isArray(blocks) ? blocks : [];
  const normalized = safeBlocks
    .filter((block) => block && typeof block === 'object')
    .map((block) => (block.id ? block : { ...block, id: createBlockId() }));
  if (normalized.length > 0) return normalized;
  return [{ id: createBlockId(), type: 'text', text: '' }];
}

function parseBlocks(raw = '') {
  return normalizeBlocks(parsePlaqueContent(raw).blocks);
}

const PHOTO_RESULTS_PAGE_SIZE = 25;
const DEFAULT_KRYTEN_PROVIDER = 'anthropic';
const DEFAULT_KRYTEN_MODEL = 'claude-sonnet-4-20250514';

function formatProviderModel(provider, model, fallback = 'Unknown model') {
  const providerLabel = String(provider || '').trim();
  const modelLabel = String(model || '').trim();
  if (!providerLabel && !modelLabel) return fallback;
  return `${providerLabel || 'AI'}${modelLabel ? ` ${modelLabel}` : ''}`.trim();
}

const normalizeCurrentRevision = (revision) => {
  if (!revision || typeof revision !== 'object') return null;
  return {
    id: Number(revision.id || 0) || null,
    revisionKind: String(revision.revision_kind || revision.revisionKind || 'manual').trim().toLowerCase() || 'manual',
    provider: revision.provider || null,
    model: revision.model || null,
    generationStatus: String(revision.generation_status || revision.generationStatus || 'generated').trim().toLowerCase() || 'generated',
    createdAt: revision.created_at || revision.createdAt || null,
  };
};

function formatRevisionSummary(revision) {
  const normalized = normalizeCurrentRevision(revision);
  if (!normalized) return '';
  const parts = [`rev ${normalized.id || 'n/a'}`];
  if (normalized.provider || normalized.model) {
    parts.push(`${normalized.provider || 'manual'}${normalized.model ? ` ${normalized.model}` : ''}`.trim());
  } else {
    parts.push(getRevisionKindLabel(normalized.revisionKind));
  }
  if (normalized.createdAt) parts.push(normalized.createdAt);
  return parts.join(' · ');
}

function getBlockCardClasses(type) {
  return BLOCK_CARD_CLASSES[type] || BLOCK_CARD_CLASSES.default;
}

function getBlockLabelClasses(type) {
  return BLOCK_LABEL_CLASSES[type] || BLOCK_LABEL_CLASSES.default;
}

function getBlockFieldClasses(type) {
  return BLOCK_FIELD_CLASSES[type] || BLOCK_FIELD_CLASSES.default;
}

function TagFilter({ value = [], suggestions = [], onChange }) {
  const handleToggle = (tag) => {
    if (value.includes(tag)) {
      onChange(value.filter((item) => item !== tag));
    } else {
      onChange([...value, tag]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {suggestions.map((tag) => (
        <button
          key={tag}
          type="button"
          className={`rounded-full px-3 py-1 text-xs ${
            value.includes(tag)
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
          }`}
          onClick={() => handleToggle(tag)}
        >
          {tag}
        </button>
      ))}
    </div>
  );
}

export default function PlaqueEditorModal({
  site,
  area,
  assets,
  plaqueId,
  onClose,
  onSaved,
  onRefreshAssets,
  onEditWidget,
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [generatingCopy, setGeneratingCopy] = useState(false);
  const [error, setError] = useState('');
  const [plaque, setPlaque] = useState(null);
  const [currentRevision, setCurrentRevision] = useState(null);
  const [activePlaqueId, setActivePlaqueId] = useState(plaqueId || null);
  const [title, setTitle] = useState('');
  const [blocks, setBlocks] = useState(() => normalizeBlocks([{ type: 'text', text: '' }]));
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [selectedLibraryAssetIds, setSelectedLibraryAssetIds] = useState([]);
  const [sourceLanguageCode, setSourceLanguageCode] = useState('en');
  const [viewLanguageCode, setViewLanguageCode] = useState('en');
  const [monetisationMode, setMonetisationMode] = useState(
    normalizeMonetisationMode(site?.defaultMonetisationMode || site?.default_monetisation_mode || 'off', 'off')
  );
  const [languageVersions, setLanguageVersions] = useState([]);
  const [compareFromCode, setCompareFromCode] = useState('en');
  const [compareToCode, setCompareToCode] = useState('vi');
  const [copyPrompt, setCopyPrompt] = useState('');
  const [copyParagraphs, setCopyParagraphs] = useState(3);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [availableMaps, setAvailableMaps] = useState([]);
  const [selectedMapId, setSelectedMapId] = useState(null);
  const [sitePlaques, setSitePlaques] = useState([]);
  const [, setMapYouAreHereOverride] = useState('');
  const [, setMapMarkerEnabled] = useState(false);
  const [ads, setAds] = useState([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [adsError, setAdsError] = useState('');
  const [adFilter, setAdFilter] = useState({ q: '', status: 'all', tags: [] });
  const [selectedAdId, setSelectedAdId] = useState(null);
  const [campaignAds, setCampaignAds] = useState([]);
  const [campaignAdsLoading, setCampaignAdsLoading] = useState(false);
  const [campaignAdsError, setCampaignAdsError] = useState('');
  const [campaignAdsFilter, setCampaignAdsFilter] = useState({ q: '' });
  const [campaignAdsDynamicMode, setCampaignAdsDynamicMode] = useState(false);
  const [selectedCampaignAdId, setSelectedCampaignAdId] = useState(null);
  const [selectedCampaignAdIds, setSelectedCampaignAdIds] = useState([]);
  const [widgets, setWidgets] = useState([]);
  const [widgetsLoading, setWidgetsLoading] = useState(false);
  const [widgetsError, setWidgetsError] = useState('');
  const [widgetFilter, setWidgetFilter] = useState({ q: '', status: 'all', partner: 'all', tags: [] });
  const [selectedWidgetId, setSelectedWidgetId] = useState(null);
  const [photoSearchQuery, setPhotoSearchQuery] = useState('');
  const [pixabayPage, setPixabayPage] = useState(1);
  const [pexelsPage, setPexelsPage] = useState(1);
  const [pixabayResults, setPixabayResults] = useState([]);
  const [pexelsResults, setPexelsResults] = useState([]);
  const [pixabayVisibleCount, setPixabayVisibleCount] = useState(PHOTO_RESULTS_PAGE_SIZE);
  const [pexelsVisibleCount, setPexelsVisibleCount] = useState(PHOTO_RESULTS_PAGE_SIZE);
  const [pixabayTotalHits, setPixabayTotalHits] = useState(0);
  const [pexelsTotalHits, setPexelsTotalHits] = useState(0);
  const [pixabayHasMore, setPixabayHasMore] = useState(false);
  const [pexelsHasMore, setPexelsHasMore] = useState(false);
  const [pixabayLoadingMore, setPixabayLoadingMore] = useState(false);
  const [pexelsLoadingMore, setPexelsLoadingMore] = useState(false);
  const [photoSearchLoading, setPhotoSearchLoading] = useState(false);
  const [photoSearchError, setPhotoSearchError] = useState('');
  const [selectedRemotePhoto, setSelectedRemotePhoto] = useState(null);
  const [importingRemotePhoto, setImportingRemotePhoto] = useState(false);
  const [pendingScrollIndex, setPendingScrollIndex] = useState(null);
  const [rightTab, setRightTab] = useState('map');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const photoBrowserRef = useRef(null);
  const [sectionName, setSectionName] = useState('');
  const [humanizePanelOpen, setHumanizePanelOpen] = useState(false);
  const [humanizeInstructions, setHumanizeInstructions] = useState('');
  const [humanizeIncludeTranslations, setHumanizeIncludeTranslations] = useState(false);
  const [humanizing, setHumanizing] = useState(false);
  const [humanizeMessage, setHumanizeMessage] = useState('');
  const [humanizeError, setHumanizeError] = useState('');
  const [translationMessage, setTranslationMessage] = useState('');
  const [translationMeta, setTranslationMeta] = useState(null);
  const [maintainTranslationTitles, setMaintainTranslationTitles] = useState(true);
  const [removingTranslationCode, setRemovingTranslationCode] = useState('');
  const [promotionPreview, setPromotionPreview] = useState(null);
  const [promotionPreviewLoading, setPromotionPreviewLoading] = useState(false);
  const [promotionPreviewError, setPromotionPreviewError] = useState('');
  const [promotionPreviewRefreshToken, setPromotionPreviewRefreshToken] = useState(0);
  const [mainPanelsOpen, setMainPanelsOpen] = useState({
    generateCopy: true,
    landingContent: true,
    translations: true,
    autoPromotions: false,
  });
  const blocksRef = useRef(null);

  const siteId = site?.id || null;
  const areaId = area?.id || null;
  const token = localStorage.getItem('token');
  const isAdmin = getUserRoleFromToken(token) === 'admin';
  const isActivityHubSurface = String(area?.area_type || area?.areaType || '').trim().toLowerCase() === 'activity_hub';
  const subscriptionType = String(
    site?.subscriptionType
    || site?.subscription_type
    || getSubscriptionTypeFromToken(token)
    || 'free'
  ).trim().toLowerCase();
  const monetisationIsEnforced = subscriptionType === 'free';
  const effectiveMonetisationMode = monetisationIsEnforced
    ? 'full'
    : normalizeMonetisationMode(
        monetisationMode,
        normalizeMonetisationMode(site?.defaultMonetisationMode || site?.default_monetisation_mode || 'off', 'off')
      );
  const savedMonetisationMode = normalizeMonetisationMode(
    plaque?.monetisationMode || plaque?.monetisation_mode || site?.defaultMonetisationMode || site?.default_monetisation_mode || 'off',
    'off'
  );

  const imageAssets = useMemo(
    () => (Array.isArray(assets) ? assets.filter((asset) => isImageAsset(asset)) : []),
    [assets]
  );

  const assetById = useMemo(() => {
    const map = new Map();
    (Array.isArray(assets) ? assets : []).forEach((asset) => {
      if (asset?.id) map.set(String(asset.id), asset);
    });
    return map;
  }, [assets]);

  const selectedLibraryAsset = useMemo(() => {
    const firstSelectedId = Array.isArray(selectedLibraryAssetIds) ? selectedLibraryAssetIds[0] : null;
    if (!firstSelectedId) return null;
    return imageAssets.find((asset) => String(asset.id) === String(firstSelectedId)) || null;
  }, [imageAssets, selectedLibraryAssetIds]);

  const selectedMap = useMemo(
    () => availableMaps.find((map) => String(map.id) === String(selectedMapId)) || null,
    [availableMaps, selectedMapId]
  );
  const mapPlaques = useMemo(
    () => sitePlaques.filter((row) => selectedMapId && String(row.map_id) === String(selectedMapId)),
    [sitePlaques, selectedMapId]
  );
  const navigablePlaques = useMemo(
    () => sitePlaques.filter((row) => row?.id),
    [sitePlaques]
  );
  const currentPlaqueIndex = useMemo(
    () => navigablePlaques.findIndex((row) => String(row.id) === String(activePlaqueId)),
    [activePlaqueId, navigablePlaques]
  );
  const hasPlaqueNavigation = Boolean(activePlaqueId) && navigablePlaques.length > 1 && currentPlaqueIndex >= 0;
  const mapLabelScale = typeof selectedMap?.labelScale === 'number' ? selectedMap.labelScale : 1;
  const mapLabelOpacity = typeof selectedMap?.labelOpacity === 'number' ? selectedMap.labelOpacity : 1;

  const legendNumberById = useMemo(() => {
    const plaquesOnMap = mapPlaques.filter(
      (plaque) => selectedMapId && String(plaque.map_id) === String(selectedMapId)
    );
    const legendPlaques = plaquesOnMap
      .filter((plaque) => plaque.map_include_in_legend)
      .sort((a, b) => {
        const aOrder = a.map_legend_order ?? Number.POSITIVE_INFINITY;
        const bOrder = b.map_legend_order ?? Number.POSITIVE_INFINITY;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return String(a.title || '').localeCompare(String(b.title || ''));
      })
      .map((plaque, index) => ({ ...plaque, legendNumber: index + 1 }));
    return new Map(legendPlaques.map((plaque) => [plaque.id, plaque.legendNumber]));
  }, [mapPlaques, selectedMapId]);

  const landingUrl = useMemo(() => {
    const key = plaque?.landing_key || plaque?.landingKey;
    if (!key) return '';
    if (typeof window === 'undefined') return `/lp/p/${key}`;
    return `${window.location.origin}/lp/p/${key}`;
  }, [plaque]);
  const previewLanguageCode = useMemo(
    () => normalizeLanguageCode(viewLanguageCode || sourceLanguageCode, sourceLanguageCode || 'en'),
    [sourceLanguageCode, viewLanguageCode]
  );
  const previewLandingUrl = useMemo(() => {
    if (!landingUrl) return '';
    const separator = landingUrl.includes('?') ? '&' : '?';
    return `${landingUrl}${separator}lang=${encodeURIComponent(previewLanguageCode)}`;
  }, [landingUrl, previewLanguageCode]);
  const landingKey = useMemo(
    () => String(plaque?.landing_key || plaque?.landingKey || '').trim(),
    [plaque]
  );

  const normalizedCurrentRevision = useMemo(
    () => normalizeCurrentRevision(currentRevision || plaque?.currentRevision || null),
    [currentRevision, plaque]
  );

  const currentRevisionSummary = useMemo(
    () => formatRevisionSummary(normalizedCurrentRevision),
    [normalizedCurrentRevision]
  );

  const currentTranslatorLabel = useMemo(
    () => formatProviderModel(translationMeta?.provider, translationMeta?.model, 'Waiting for a translation run'),
    [translationMeta]
  );

  const sidebarTabs = useMemo(() => {
    const baseTabs = [
      { key: 'preview', label: 'Preview', icon: Eye },
      { key: 'map', label: 'Map & QR', icon: MapIcon },
      { key: 'campaignAds', label: 'Campaign Ads', icon: Flag },
      { key: 'assets', label: 'Assets', icon: ImageIcon },
      { key: 'photos', label: 'Photos', icon: Search },
    ];
    if (isAdmin) {
      baseTabs.splice(3, 0,
        { key: 'ads', label: 'Ads', icon: Megaphone },
        { key: 'widgets', label: 'Widgets', icon: PanelTop }
      );
    }
    return baseTabs;
  }, [isAdmin]);

  const sourceBodyText = useMemo(
    () => serializePlaqueContent({ blocks }),
    [blocks]
  );

  const allLanguageCodes = useMemo(() => {
    const codes = new Set(LANGUAGE_OPTIONS.map((option) => option.code));
    languageVersions.forEach((row) => {
      const code = normalizeLanguageCode(row.languageCode);
      if (code) codes.add(code);
    });
    const sourceCode = normalizeLanguageCode(sourceLanguageCode);
    if (sourceCode) codes.add(sourceCode);
    return Array.from(codes);
  }, [languageVersions, sourceLanguageCode]);

  const languageVersionMap = useMemo(() => {
    const map = new Map();
    languageVersions.forEach((row) => {
      if (row?.languageCode) map.set(row.languageCode.toLowerCase(), row);
    });
    return map;
  }, [languageVersions]);

  const sourceVersion = useMemo(
    () => languageVersions.find((row) => sameLanguageCode(row.languageCode, sourceLanguageCode)) || null,
    [languageVersions, sourceLanguageCode]
  );

  const viewVersion = useMemo(
    () => languageVersions.find((row) => sameLanguageCode(row.languageCode, viewLanguageCode)) || null,
    [languageVersions, viewLanguageCode]
  );

  const compareFromVersion = useMemo(
    () => languageVersionMap.get(normalizeLanguageCode(compareFromCode).toLowerCase()) || null,
    [compareFromCode, languageVersionMap]
  );

  const compareToVersion = useMemo(
    () => languageVersionMap.get(normalizeLanguageCode(compareToCode).toLowerCase()) || null,
    [compareToCode, languageVersionMap]
  );

  const adsById = useMemo(() => {
    const map = new Map();
    (Array.isArray(ads) ? ads : []).forEach((ad) => {
      if (ad?.id) map.set(String(ad.id), ad);
    });
    return map;
  }, [ads]);

  const campaignAdsById = useMemo(() => {
    const map = new Map();
    (Array.isArray(campaignAds) ? campaignAds : []).forEach((campaignAd) => {
      if (campaignAd?.campaignId) map.set(String(campaignAd.campaignId), campaignAd);
    });
    return map;
  }, [campaignAds]);

  const widgetsById = useMemo(() => {
    const map = new Map();
    (Array.isArray(widgets) ? widgets : []).forEach((widget) => {
      if (widget?.id) map.set(String(widget.id), widget);
    });
    return map;
  }, [widgets]);

  const allAdTags = useMemo(() => {
    const set = new Set();
    (Array.isArray(ads) ? ads : []).forEach((ad) => {
      if (Array.isArray(ad.tags)) {
        ad.tags.forEach((tag) => {
          if (tag) set.add(tag);
        });
      }
    });
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [ads]);

  const filteredAds = useMemo(() => {
    const q = String(adFilter.q || '').trim().toLowerCase();
    return (Array.isArray(ads) ? ads : []).filter((ad) => {
      const matchesStatus = adFilter.status === 'all' || ad.status === adFilter.status;
      const matchesTags =
        adFilter.tags.length === 0 ||
        (Array.isArray(ad.tags) && adFilter.tags.every((tag) => ad.tags.includes(tag)));
      const matchesSearch = !q || String(ad.title || '').toLowerCase().includes(q)
        || String(ad.body || '').toLowerCase().includes(q);
      return matchesStatus && matchesTags && matchesSearch;
    });
  }, [ads, adFilter]);

  const filteredCampaignAds = useMemo(() => {
    const q = String(campaignAdsFilter.q || '').trim().toLowerCase();
    return (Array.isArray(campaignAds) ? campaignAds : []).filter((campaignAd) => {
      if (!q) return true;
      return String(campaignAd.title || '').toLowerCase().includes(q)
        || String(campaignAd.siteName || '').toLowerCase().includes(q)
        || String(campaignAd.landingKey || '').toLowerCase().includes(q);
    });
  }, [campaignAds, campaignAdsFilter]);

  const selectedCampaignAds = useMemo(
    () => (Array.isArray(selectedCampaignAdIds) ? selectedCampaignAdIds : [])
      .map((campaignId) => campaignAdsById.get(String(campaignId)) || null)
      .filter(Boolean),
    [campaignAdsById, selectedCampaignAdIds]
  );

  const allWidgetTags = useMemo(() => {
    const set = new Set();
    (Array.isArray(widgets) ? widgets : []).forEach((widget) => {
      if (Array.isArray(widget.tags)) {
        widget.tags.forEach((tag) => {
          if (tag) set.add(tag);
        });
      }
    });
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
  }, [widgets]);

  const filteredWidgets = useMemo(() => {
    const q = String(widgetFilter.q || '').trim().toLowerCase();
    return (Array.isArray(widgets) ? widgets : []).filter((widget) => {
      const matchesStatus = widgetFilter.status === 'all' || widget.status === widgetFilter.status;
      const matchesPartner = widgetFilter.partner === 'all' || widget.partner === widgetFilter.partner;
      const matchesTags =
        widgetFilter.tags.length === 0 ||
        (Array.isArray(widget.tags) && widgetFilter.tags.every((tag) => widget.tags.includes(tag)));
      const matchesSearch = !q || String(widget.title || '').toLowerCase().includes(q)
        || String(widget.embed_html || '').toLowerCase().includes(q);
      return matchesStatus && matchesPartner && matchesTags && matchesSearch;
    });
  }, [widgets, widgetFilter]);

  const selectedAd = useMemo(
    () => (selectedAdId ? adsById.get(String(selectedAdId)) || null : null),
    [adsById, selectedAdId]
  );

  const selectedCampaignAd = useMemo(
    () => (selectedCampaignAdId ? campaignAdsById.get(String(selectedCampaignAdId)) || null : null),
    [campaignAdsById, selectedCampaignAdId]
  );

  const selectedWidget = useMemo(
    () => (selectedWidgetId ? widgetsById.get(String(selectedWidgetId)) || null : null),
    [widgetsById, selectedWidgetId]
  );

  useEffect(() => {
    let cancelled = false;

    async function generateQr() {
      if (!landingUrl) {
        setQrDataUrl('');
        return;
      }
      try {
        const dataUrl = await QRCode.toDataURL(landingUrl, { errorCorrectionLevel: 'M', margin: 2, width: 520 });
        if (!cancelled) setQrDataUrl(dataUrl);
      } catch (err) {
        console.warn('Failed to generate QR code', err);
        if (!cancelled) setQrDataUrl('');
      }
    }

    generateQr();

    return () => {
      cancelled = true;
    };
  }, [landingUrl]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const { documentElement, body } = document;
    const previousHtmlOverflow = documentElement.style.overflow;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyOverscrollBehavior = body.style.overscrollBehavior;

    documentElement.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'contain';

    return () => {
      documentElement.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
      body.style.overscrollBehavior = previousBodyOverscrollBehavior;
    };
  }, []);

  useEffect(() => {
    setActivePlaqueId(plaqueId || null);
  }, [plaqueId]);

  useEffect(() => {
    let cancelled = false;
    async function loadSiteMaps() {
      if (!siteId) {
        setAvailableMaps([]);
        setSelectedMapId(null);
        return;
      }
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`/api/site-maps?siteId=${encodeURIComponent(siteId)}`, {
          cache: 'no-store',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new Error(body || `Failed to load site maps (${res.status})`);
        }
        const data = await res.json().catch(() => ({}));
        const maps = Array.isArray(data.maps) ? data.maps : [];
        if (cancelled) return;
        setAvailableMaps(maps);
        if (selectedMapId && !maps.find((map) => String(map.id) === String(selectedMapId))) {
          setSelectedMapId(maps.length ? maps[0].id : null);
        }
      } catch (err) {
        console.error('Failed to load site maps', err);
        if (!cancelled) {
          setAvailableMaps([]);
          setSelectedMapId(null);
        }
      }
    }
    loadSiteMaps();
    return () => {
      cancelled = true;
    };
  }, [siteId, selectedMapId]);

  useEffect(() => {
    let cancelled = false;
    async function loadSitePlaques() {
      if (!siteId) {
        setSitePlaques([]);
        return;
      }
      const token = localStorage.getItem('token');
      try {
        const params = new URLSearchParams({ siteId: String(siteId) });
        if (areaId) params.set('areaId', String(areaId));
        const res = await fetch(`/api/plaques?${params.toString()}`, {
          cache: 'no-store',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new Error(body || `Failed to load plaques (${res.status})`);
        }
        const data = await res.json().catch(() => ({}));
        const rows = Array.isArray(data.plaques) ? data.plaques : [];
        if (!cancelled) {
          setSitePlaques(rows);
        }
      } catch (err) {
        console.error('Failed to load site plaques', err);
        if (!cancelled) setSitePlaques([]);
      }
    }
    loadSitePlaques();
    return () => {
      cancelled = true;
    };
  }, [areaId, siteId]);

  useEffect(() => {
    setLanguageVersions((prev) => {
      const sourceCode = normalizeLanguageCode(sourceLanguageCode, 'en');
      const editorCode = normalizeLanguageCode(viewLanguageCode || sourceCode, sourceCode);
      const otherRows = prev.filter((row) => !sameLanguageCode(row.languageCode, editorCode));
      const existingEditorRow = prev.find((row) => sameLanguageCode(row.languageCode, editorCode)) || null;
      const isSourceRow = sameLanguageCode(editorCode, sourceCode);
      return sortLanguageVersions([
        {
          languageCode: editorCode,
          isSource: isSourceRow,
          title: title || '',
          bodyText: sourceBodyText || '',
          translatedText: sourceBodyText || '',
          mapYouAreHereLabel: existingEditorRow?.mapYouAreHereLabel || '',
          autoGenerated: false,
          reviewStatus: 'draft',
          freshness: isSourceRow ? 'source' : 'current',
          isOutOfDate: false,
          sourceContentUpdatedAt: plaque?.sourceContentUpdatedAt || plaque?.source_content_updated_at || existingEditorRow?.sourceContentUpdatedAt || null,
          contentUpdatedAt: plaque?.sourceContentUpdatedAt || plaque?.source_content_updated_at || plaque?.updated_at || plaque?.updatedAt || existingEditorRow?.contentUpdatedAt || null,
        },
        ...otherRows.map((row) => ({
          ...row,
          isSource: sameLanguageCode(row.languageCode, sourceCode),
          freshness: sameLanguageCode(row.languageCode, sourceCode) ? 'source' : (row.freshness || 'current'),
          isOutOfDate: sameLanguageCode(row.languageCode, sourceCode) ? false : Boolean(row.isOutOfDate),
        })),
      ], sourceCode);
    });
  }, [blocks, plaque, sourceBodyText, sourceLanguageCode, title, viewLanguageCode]);

  const loadEditorLanguage = useCallback((languageCode, rows, fallbackPlaque = plaque) => {
    const normalizedLanguageCode = normalizeLanguageCode(languageCode, sourceLanguageCode || 'en');
    const availableRows = Array.isArray(rows) ? rows : [];
    const row = availableRows.find((item) => sameLanguageCode(item.languageCode, normalizedLanguageCode)) || null;
    const fallbackTitle = sameLanguageCode(normalizedLanguageCode, sourceLanguageCode)
      ? (fallbackPlaque?.title || '')
      : '';
    const fallbackBody = sameLanguageCode(normalizedLanguageCode, sourceLanguageCode)
      ? (fallbackPlaque?.english_text || fallbackPlaque?.englishText || '')
      : '';
    setViewLanguageCode(normalizedLanguageCode);
    setTitle(row?.title || fallbackTitle);
    setBlocks(parseBlocks(row?.bodyText || row?.translatedText || fallbackBody));
  }, [plaque, sourceLanguageCode]);

  useEffect(() => {
    const sourceCode = normalizeLanguageCode(sourceLanguageCode, 'en');
    if (!normalizeLanguageCode(compareFromCode) || !allLanguageCodes.some((code) => sameLanguageCode(code, compareFromCode))) {
      setCompareFromCode(sourceCode);
      return;
    }
    if (!normalizeLanguageCode(compareToCode) || !allLanguageCodes.some((code) => sameLanguageCode(code, compareToCode)) || sameLanguageCode(compareToCode, compareFromCode)) {
      const fallbackCandidates = [
        getDefaultCompareToLanguageCode(languageVersions, sourceCode),
        ...allLanguageCodes,
      ];
      const fallbackCode = fallbackCandidates.find((code) => !sameLanguageCode(code, compareFromCode)) || sourceCode;
      setCompareToCode(fallbackCode);
    }
  }, [allLanguageCodes, compareFromCode, compareToCode, languageVersions, sourceLanguageCode]);

  useEffect(() => {
    const normalizedViewCode = normalizeLanguageCode(viewLanguageCode);
    if (normalizedViewCode && allLanguageCodes.some((code) => sameLanguageCode(code, normalizedViewCode))) return;
    loadEditorLanguage(sourceLanguageCode, languageVersions, plaque);
  }, [allLanguageCodes, languageVersions, loadEditorLanguage, plaque, sourceLanguageCode, viewLanguageCode]);

  useEffect(() => {
    let cancelled = false;

    async function loadPromotionPreview() {
      if (isActivityHubSurface || !activePlaqueId || !landingKey) {
        setPromotionPreview(null);
        setPromotionPreviewError('');
        setPromotionPreviewLoading(false);
        return;
      }

      setPromotionPreviewLoading(true);
      setPromotionPreviewError('');
      try {
        const res = await fetch(
          `/lp/api/plaque/${encodeURIComponent(landingKey)}?lang=${encodeURIComponent(previewLanguageCode)}`,
          { cache: 'no-store' }
        );
        if (!res.ok) {
          const body = await res.text().catch(() => '');
          throw new Error(body || `Failed to load monetisation preview (${res.status})`);
        }
        const data = await res.json().catch(() => ({}));
        if (!cancelled) {
          setPromotionPreview(data);
        }
      } catch (err) {
        console.error('Failed to load monetisation preview', err);
        if (!cancelled) {
          setPromotionPreview(null);
          setPromotionPreviewError(err.message || 'Failed to load monetisation preview.');
        }
      } finally {
        if (!cancelled) setPromotionPreviewLoading(false);
      }
    }

    loadPromotionPreview();

    return () => {
      cancelled = true;
    };
  }, [activePlaqueId, isActivityHubSurface, landingKey, previewLanguageCode, promotionPreviewRefreshToken]);

  const loadPlaque = useCallback(async () => {
    if (!activePlaqueId) {
      setPlaque(null);
      setCurrentRevision(null);
      setTitle('');
      setBlocks(normalizeBlocks([{ type: 'text', text: '' }]));
      setSelectedAssetId(null);
      setSelectedLibraryAssetIds([]);
      setSourceLanguageCode('en');
      setViewLanguageCode('en');
      setMonetisationMode(
        normalizeMonetisationMode(site?.defaultMonetisationMode || site?.default_monetisation_mode || 'off', 'off')
      );
      setLanguageVersions([]);
      setCompareFromCode('en');
      setCompareToCode('vi');
      setMapYouAreHereOverride('');
      setMapMarkerEnabled(false);
      setSelectedMapId(null);
      setSectionName('');
      setHumanizeMessage('');
      setHumanizeError('');
      return;
    }
    const token = localStorage.getItem('token');
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/plaques/${activePlaqueId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to load plaque (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      const fetched = data.plaque || data;
      setPlaque(fetched);
      setCurrentRevision(normalizeCurrentRevision(data.currentRevision || fetched.currentRevision || null));
      setTitle(fetched.title || '');
      setMonetisationMode(
        normalizeMonetisationMode(fetched.monetisationMode || fetched.monetisation_mode || site?.defaultMonetisationMode || 'off', 'off')
      );

      const englishText = fetched.english_text || fetched.englishText || '';
      setBlocks(parseBlocks(englishText));

      const heroAssetId = fetched.selectedAssetId || null;
      setSelectedAssetId(heroAssetId);
      setSelectedLibraryAssetIds([]);

      const incomingMapId = fetched.map_id || fetched.mapId || null;
      setSelectedMapId(incomingMapId ? String(incomingMapId) : null);

      const normalizedSourceLanguageCode = normalizeLanguageCode(
        fetched.sourceLanguageCode || fetched.source_language_code,
        'en'
      );
      const normalizedLanguageVersions = normalizeLanguageVersions({
        plaque: fetched,
        translations: data.translations || fetched.translations || [],
        languageVersions: data.languageVersions || fetched.languageVersions || [],
      });
      setSourceLanguageCode(normalizedSourceLanguageCode);
      setViewLanguageCode(normalizedSourceLanguageCode);
      setLanguageVersions(normalizedLanguageVersions);
      setCompareFromCode(normalizedSourceLanguageCode);
      setCompareToCode(getDefaultCompareToLanguageCode(normalizedLanguageVersions, normalizedSourceLanguageCode));
    } catch (err) {
      console.error('Failed to load plaque', err);
      setError(err.message || 'Failed to load plaque.');
    } finally {
      setLoading(false);
    }
  }, [activePlaqueId, site?.defaultMonetisationMode, site?.default_monetisation_mode]);

  const loadAds = useCallback(async () => {
    if (!isAdmin) {
      setAds([]);
      setAdsError('');
      setAdsLoading(false);
      return;
    }
    const token = localStorage.getItem('token');
    setAdsLoading(true);
    setAdsError('');
    try {
      const res = await fetch('/api/creative/ads', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to load ads (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      const rows = Array.isArray(data.ads) ? data.ads : [];
      setAds(rows);
    } catch (err) {
      console.error('Failed to load ads', err);
      setAds([]);
      setAdsError(err.message || 'Failed to load ads.');
    } finally {
      setAdsLoading(false);
    }
  }, [isAdmin]);

  const loadCampaignAds = useCallback(async () => {
    setCampaignAdsLoading(true);
    setCampaignAdsError('');
    try {
      const res = await fetch('/api/creative/campaign-ads', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to load campaign ads (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      const rows = Array.isArray(data.campaignAds) ? data.campaignAds : [];
      setCampaignAds(rows);
    } catch (err) {
      console.error('Failed to load campaign ads', err);
      setCampaignAds([]);
      setCampaignAdsError(err.message || 'Failed to load campaign ads.');
    } finally {
      setCampaignAdsLoading(false);
    }
  }, [token]);

  const loadWidgets = useCallback(async () => {
    if (!isAdmin) {
      setWidgets([]);
      setWidgetsError('');
      setWidgetsLoading(false);
      return;
    }
    const token = localStorage.getItem('token');
    setWidgetsLoading(true);
    setWidgetsError('');
    try {
      const res = await fetch('/api/creative/widgets', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Failed to load widgets (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      const rows = Array.isArray(data.widgets) ? data.widgets : [];
      setWidgets(rows);
    } catch (err) {
      console.error('Failed to load widgets', err);
      setWidgets([]);
      setWidgetsError(err.message || 'Failed to load widgets.');
    } finally {
      setWidgetsLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    loadAds();
  }, [loadAds]);

  useEffect(() => {
    loadCampaignAds();
  }, [loadCampaignAds]);

  useEffect(() => {
    if (campaignAdsDynamicMode) {
      setSelectedCampaignAdIds((prev) => {
        const current = Array.isArray(prev) ? prev : [];
        if (current.length) return current;
        return selectedCampaignAdId ? [selectedCampaignAdId] : [];
      });
      return;
    }
    if (!selectedCampaignAdId && selectedCampaignAdIds.length) {
      setSelectedCampaignAdId(selectedCampaignAdIds[0]);
    }
  }, [campaignAdsDynamicMode, selectedCampaignAdId, selectedCampaignAdIds]);

  useEffect(() => {
    loadWidgets();
  }, [loadWidgets]);

  useEffect(() => {
    if (!isAdmin && (rightTab === 'ads' || rightTab === 'widgets')) {
      setRightTab('map');
    }
  }, [isAdmin, rightTab]);

  const mergeUniqueById = useCallback((existingRows, incomingRows) => {
    const map = new Map();
    (Array.isArray(existingRows) ? existingRows : []).forEach((row) => {
      if (row?.id != null) map.set(String(row.id), row);
    });
    (Array.isArray(incomingRows) ? incomingRows : []).forEach((row) => {
      if (row?.id != null) map.set(String(row.id), row);
    });
    return Array.from(map.values());
  }, []);

  const fetchPhotoProviderPage = useCallback(async (provider, query, page, perPage = PHOTO_RESULTS_PAGE_SIZE) => {
    const token = localStorage.getItem('token');
    const endpoint = provider === 'pixabay'
      ? '/api/creative/photos/pixabay/search'
      : '/api/creative/photos/pexels/search';
    const res = await fetch(
      `${endpoint}?q=${encodeURIComponent(query)}&page=${page}&perPage=${perPage}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`${provider}: ${body || res.status}`);
    }
    return res.json().catch(() => ({}));
  }, []);

  const runPhotoSearch = useCallback(async () => {
    const query = String(photoSearchQuery || '').trim();
    if (!query) {
      setPhotoSearchError('Enter a search term.');
      setPixabayResults([]);
      setPexelsResults([]);
      setPixabayVisibleCount(PHOTO_RESULTS_PAGE_SIZE);
      setPexelsVisibleCount(PHOTO_RESULTS_PAGE_SIZE);
      setPixabayTotalHits(0);
      setPexelsTotalHits(0);
      setPixabayPage(1);
      setPexelsPage(1);
      setPixabayHasMore(false);
      setPexelsHasMore(false);
      return;
    }
    setPhotoSearchLoading(true);
    setPhotoSearchError('');
    setSelectedRemotePhoto(null);
    try {
      const [pixabayData, pexelsData] = await Promise.all([
        fetchPhotoProviderPage('pixabay', query, 1, PHOTO_RESULTS_PAGE_SIZE),
        fetchPhotoProviderPage('pexels', query, 1, PHOTO_RESULTS_PAGE_SIZE),
      ]);

      const pixRows = Array.isArray(pixabayData.results) ? pixabayData.results : [];
      const pexRows = Array.isArray(pexelsData.results) ? pexelsData.results : [];
      const pixTotal = Number(pixabayData.totalHits) || 0;
      const pexTotal = Number(pexelsData.totalHits) || 0;

      setPixabayResults(pixRows);
      setPexelsResults(pexRows);
      setPixabayVisibleCount(Math.min(PHOTO_RESULTS_PAGE_SIZE, pixRows.length));
      setPexelsVisibleCount(Math.min(PHOTO_RESULTS_PAGE_SIZE, pexRows.length));
      setPixabayTotalHits(pixTotal);
      setPexelsTotalHits(pexTotal);
      setPixabayPage(1);
      setPexelsPage(1);
      setPixabayHasMore(pixRows.length < pixTotal);
      setPexelsHasMore(pexRows.length < pexTotal);
    } catch (err) {
      console.error('Combined photo search failed', err);
      setPixabayResults([]);
      setPexelsResults([]);
      setPixabayVisibleCount(PHOTO_RESULTS_PAGE_SIZE);
      setPexelsVisibleCount(PHOTO_RESULTS_PAGE_SIZE);
      setPixabayTotalHits(0);
      setPexelsTotalHits(0);
      setPixabayHasMore(false);
      setPexelsHasMore(false);
      setPhotoSearchError(err.message || 'Photo search failed.');
    } finally {
      setPhotoSearchLoading(false);
    }
  }, [fetchPhotoProviderPage, photoSearchQuery]);

  const loadMorePixabay = useCallback(async () => {
    if (photoSearchLoading || pixabayLoadingMore) return;
    if (pixabayVisibleCount < pixabayResults.length) {
      setPixabayVisibleCount((prev) => Math.min(prev + PHOTO_RESULTS_PAGE_SIZE, pixabayResults.length));
      return;
    }
    if (!pixabayHasMore) return;
    const query = String(photoSearchQuery || '').trim();
    if (!query) return;
    setPixabayLoadingMore(true);
    try {
      const nextPage = pixabayPage + 1;
      const data = await fetchPhotoProviderPage('pixabay', query, nextPage, PHOTO_RESULTS_PAGE_SIZE);
      const incoming = Array.isArray(data.results) ? data.results : [];
      const total = Number(data.totalHits) || pixabayTotalHits;
      setPixabayResults((prev) => {
        const merged = mergeUniqueById(prev, incoming);
        setPixabayVisibleCount((count) => Math.min(count + PHOTO_RESULTS_PAGE_SIZE, merged.length));
        setPixabayHasMore(merged.length < total);
        return merged;
      });
      setPixabayTotalHits(total);
      setPixabayPage(nextPage);
    } catch (err) {
      console.error('Pixabay load more failed', err);
      setPhotoSearchError(err.message || 'Failed to load more Pixabay results.');
    } finally {
      setPixabayLoadingMore(false);
    }
  }, [
    fetchPhotoProviderPage,
    mergeUniqueById,
    photoSearchLoading,
    photoSearchQuery,
    pixabayHasMore,
    pixabayLoadingMore,
    pixabayPage,
    pixabayResults.length,
    pixabayTotalHits,
    pixabayVisibleCount,
  ]);

  const loadMorePexels = useCallback(async () => {
    if (photoSearchLoading || pexelsLoadingMore) return;
    if (pexelsVisibleCount < pexelsResults.length) {
      setPexelsVisibleCount((prev) => Math.min(prev + PHOTO_RESULTS_PAGE_SIZE, pexelsResults.length));
      return;
    }
    if (!pexelsHasMore) return;
    const query = String(photoSearchQuery || '').trim();
    if (!query) return;
    setPexelsLoadingMore(true);
    try {
      const nextPage = pexelsPage + 1;
      const data = await fetchPhotoProviderPage('pexels', query, nextPage, PHOTO_RESULTS_PAGE_SIZE);
      const incoming = Array.isArray(data.results) ? data.results : [];
      const total = Number(data.totalHits) || pexelsTotalHits;
      setPexelsResults((prev) => {
        const merged = mergeUniqueById(prev, incoming);
        setPexelsVisibleCount((count) => Math.min(count + PHOTO_RESULTS_PAGE_SIZE, merged.length));
        setPexelsHasMore(merged.length < total);
        return merged;
      });
      setPexelsTotalHits(total);
      setPexelsPage(nextPage);
    } catch (err) {
      console.error('Pexels load more failed', err);
      setPhotoSearchError(err.message || 'Failed to load more Pexels results.');
    } finally {
      setPexelsLoadingMore(false);
    }
  }, [
    fetchPhotoProviderPage,
    mergeUniqueById,
    pexelsHasMore,
    pexelsLoadingMore,
    pexelsPage,
    pexelsResults.length,
    pexelsTotalHits,
    pexelsVisibleCount,
    photoSearchLoading,
    photoSearchQuery,
  ]);

  const resolvePhotoPreviewUrl = useCallback((photo) => {
    if (!photo) return '';
    return (
      photo.thumbnailProxyUrl ||
      photo.previewProxyUrl ||
      photo.webformatProxyUrl ||
      photo.largeImageProxyUrl ||
      photo.thumbnailUrl ||
      photo.previewUrl ||
      photo.webformatUrl ||
      photo.largeImageUrl ||
      ''
    );
  }, []);

  const handlePhotoPreviewError = useCallback((event, photo) => {
    const img = event?.currentTarget;
    if (!img || !photo) return;
    const current = img.getAttribute('src') || '';
    const nextCandidates = [
      photo.thumbnailProxyUrl || '',
      photo.previewProxyUrl || '',
      photo.webformatProxyUrl || '',
      photo.largeImageProxyUrl || '',
      photo.previewUrl || '',
      photo.webformatUrl || '',
      photo.largeImageUrl || '',
    ].filter(Boolean);
    const next = nextCandidates.find((candidate) => candidate !== current) || '';
    if (!next) return;
    img.setAttribute('src', next);
  }, []);

  const addImageBlockFromAsset = useCallback((assetId, defaults = {}) => {
    if (!assetId) return;
    const imageBlock = {
      id: createBlockId(),
      type: 'image',
      assetId: Number(assetId),
      linkUrl: '',
      alt: String(defaults.alt || '').trim(),
      caption: String(defaults.caption || '').trim(),
    };
    const currentViewCode = normalizeLanguageCode(viewLanguageCode || sourceLanguageCode, sourceLanguageCode || 'en');

    setBlocks((prev) => [...prev, imageBlock]);
    setLanguageVersions((prev) => sortLanguageVersions(
      prev.map((row) => {
        if (!row?.languageCode || sameLanguageCode(row.languageCode, currentViewCode)) {
          return row;
        }
        const existingBody = row.bodyText || row.translatedText || '';
        const parsed = parsePlaqueContent(existingBody);
        const nextBody = serializePlaqueContent({ blocks: [...parsed.blocks, imageBlock] });
        return {
          ...row,
          bodyText: nextBody,
          translatedText: nextBody,
          autoGenerated: false,
          reviewStatus: 'draft',
          freshness: row.isSource ? 'source' : 'current',
          isOutOfDate: false,
        };
      }),
      normalizeLanguageCode(sourceLanguageCode, 'en')
    ));
  }, [sourceLanguageCode, viewLanguageCode]);

  const selectRemotePhoto = useCallback((provider, photo) => {
    if (!photo) return;
    setSelectedRemotePhoto({
      provider,
      ...photo,
    });
  }, []);

  const importSelectedRemotePhoto = useCallback(async () => {
    if (!siteId) {
      alert('Select a site first.');
      return;
    }
    if (!selectedRemotePhoto) return;
    const sourceUrl =
      selectedRemotePhoto.largeImageUrl ||
      selectedRemotePhoto.webformatUrl ||
      selectedRemotePhoto.previewUrl ||
      selectedRemotePhoto.thumbnailUrl ||
      selectedRemotePhoto.pageUrl ||
      '';
    if (!sourceUrl) {
      alert('Selected photo is missing a source URL.');
      return;
    }
    const token = localStorage.getItem('token');
    setImportingRemotePhoto(true);
    setPhotoSearchError('');
    try {
      const res = await fetch('/api/creative/photos/import', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          siteId,
          provider: selectedRemotePhoto.provider,
          imageUrl: sourceUrl,
          title: selectedRemotePhoto.tags || `${selectedRemotePhoto.provider || 'photo'} image`,
          folder: 'Photos',
        }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Import failed (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      const importedAssetId = data?.asset?.id || null;
      if (!importedAssetId) throw new Error('Import succeeded but no asset ID was returned.');
      setSelectedLibraryAssetIds([importedAssetId]);
      addImageBlockFromAsset(importedAssetId, {
        alt: selectedRemotePhoto.tags || '',
        caption: selectedRemotePhoto.tags || '',
      });
      onRefreshAssets?.();
    } catch (err) {
      console.error('Remote photo import failed', err);
      setPhotoSearchError(err.message || 'Failed to import selected photo.');
    } finally {
      setImportingRemotePhoto(false);
    }
  }, [addImageBlockFromAsset, onRefreshAssets, selectedRemotePhoto, siteId]);

  const addSection = () => {
    const trimmed = (sectionName || '').trim();
    if (!trimmed) return;
    setBlocks((prev) => [...prev, { id: createBlockId(), type: 'section', title: trimmed }]);
    setSectionName('');
  };

  const getSectionBlocks = () => {
    const safe = Array.isArray(blocks) ? blocks : [];
    return safe.map((block, index) => ({ block, index })).filter(({ block }) => block?.type === 'section');
  };

  useEffect(() => {
    loadPlaque();
  }, [loadPlaque]);

  const close = () => {
    if (saving || translating || humanizing) return;
    onClose?.();
  };

  const addTextBlock = () => {
    setBlocks((prev) => [...prev, { id: createBlockId(), type: 'text', text: '', linkTitle: '', linkUrl: '' }]);
  };

  const addSubtitleBlock = () => {
    setBlocks((prev) => [...prev, { id: createBlockId(), type: 'subtitle', text: '', size: 'h2', linkUrl: '' }]);
  };

  const addAdBlock = (size = 'inline') => {
    if (!selectedAdId) {
      alert('Select an ad from the library first.');
      return;
    }
    setBlocks((prev) => {
      const next = [...prev, { id: createBlockId(), type: 'ad', adId: selectedAdId, size, stick: '', showText: true }];
      setPendingScrollIndex(next.length - 1);
      return next;
    });
  };

  const addWidgetBlock = () => {
    if (!selectedWidgetId) {
      alert('Select a widget from the library first.');
      return;
    }
    setBlocks((prev) => {
      const next = [...prev, { id: createBlockId(), type: 'widget', widgetId: selectedWidgetId }];
      setPendingScrollIndex(next.length - 1);
      return next;
    });
  };

  const addCampaignBlock = () => {
    if (!selectedCampaignAdId) {
      alert('Select a campaign ad from the library first.');
      return;
    }
    setBlocks((prev) => {
      const next = [...prev, { id: createBlockId(), type: 'campaign', campaignId: selectedCampaignAdId }];
      setPendingScrollIndex(next.length - 1);
      return next;
    });
  };

  const addDynamicCampaignBlock = () => {
    const campaignIds = (Array.isArray(selectedCampaignAdIds) ? selectedCampaignAdIds : [])
      .map((campaignId) => Number(campaignId))
      .filter((campaignId) => Number.isFinite(campaignId) && campaignId > 0);
    if (!campaignIds.length) {
      alert('Select one or more campaign ads from the library first.');
      return;
    }
    setBlocks((prev) => {
      const next = [...prev, { id: createBlockId(), type: 'campaign_dynamic', campaignIds }];
      setPendingScrollIndex(next.length - 1);
      return next;
    });
  };

  const toggleCampaignAdSelection = useCallback((campaignId) => {
    const normalizedCampaignId = Number(campaignId || 0) || null;
    if (!normalizedCampaignId) return;
    if (campaignAdsDynamicMode) {
      setSelectedCampaignAdIds((prev) => {
        const current = Array.isArray(prev) ? prev : [];
        const exists = current.some((id) => String(id) === String(normalizedCampaignId));
        const next = exists
          ? current.filter((id) => String(id) !== String(normalizedCampaignId))
          : [...current, normalizedCampaignId];
        setSelectedCampaignAdId(next.length ? next[0] : null);
        return next;
      });
      return;
    }
    setSelectedCampaignAdId(normalizedCampaignId);
    setSelectedCampaignAdIds([normalizedCampaignId]);
  }, [campaignAdsDynamicMode]);

  const addImageBlockFromSelection = () => {
    const assetIds = Array.isArray(selectedLibraryAssetIds)
      ? selectedLibraryAssetIds.filter((assetId) => Number(assetId))
      : [];
    if (!assetIds.length) return;
    setBlocks((prev) => [
      ...prev,
      ...assetIds.map((assetId) => ({
        id: createBlockId(),
        type: 'image',
        assetId: Number(assetId),
        linkUrl: '',
        alt: '',
        caption: '',
      })),
    ]);
  };

  const toggleLibraryAssetSelection = useCallback((asset) => {
    const assetId = asset?.id || null;
    if (!assetId) return;
    setSelectedLibraryAssetIds((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const exists = current.some((id) => String(id) === String(assetId));
      if (exists) {
        return current.filter((id) => String(id) !== String(assetId));
      }
      return [...current, assetId];
    });
  }, []);

  const moveBlock = (index, direction) => {
    setBlocks((prev) => {
      const next = moveListItemByOffset(prev, index, direction);
      if (next === prev) return prev;
      const target = Math.max(0, Math.min(next.length - 1, index + direction));
      setPendingScrollIndex(target);
      return next;
    });
  };

  const moveBlockTo = (index, targetIndex) => {
    setBlocks((prev) => {
      const next = [...prev];
      const clampedTarget = Math.max(0, Math.min(next.length - 1, targetIndex));
      const [item] = next.splice(index, 1);
      next.splice(clampedTarget, 0, item);
      setPendingScrollIndex(clampedTarget);
      return next;
    });
  };

  const removeBlock = (index) => {
    setBlocks((prev) => prev.filter((_, idx) => idx !== index));
  };

  const updateBlock = (index, patch) => {
    setBlocks((prev) => prev.map((block, idx) => (idx === index ? { ...block, ...patch } : block)));
  };

  const updateBlockText = (index, text) => {
    updateBlock(index, { text });
  };

  useEffect(() => {
    if (pendingScrollIndex === null || pendingScrollIndex === undefined) return;
    const targetIndex = pendingScrollIndex;
    window.setTimeout(() => {
      const container = blocksRef.current;
      if (!container) return;
      const target = container.querySelector(`[data-block-index="${targetIndex}"]`);
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      }
    }, 80);
    setPendingScrollIndex(null);
  }, [pendingScrollIndex, blocks]);

  const buildSavePayload = useCallback(() => {
    const currentEditorText = serializePlaqueContent({ blocks });
    const resolvedAreaId = area?.id || plaque?.area_id || plaque?.areaId || null;
    const sectionBlocks = (Array.isArray(blocks) ? blocks : [])
      .map((block, idx) => (block?.type === 'section'
        ? {
            name: typeof block.title === 'string' ? block.title.trim() : '',
            enabled: true,
            sort_order: idx + 1,
          }
        : null))
      .filter((section) => section && section.name);

    const normalizedSourceLanguageCode = normalizeLanguageCode(sourceLanguageCode, 'en');
    const normalizedViewLanguageCode = normalizeLanguageCode(viewLanguageCode || normalizedSourceLanguageCode, normalizedSourceLanguageCode);
    const normalizedLanguageVersions = sortLanguageVersions([
      ...languageVersions
        .filter((row) => !sameLanguageCode(row.languageCode, normalizedViewLanguageCode))
        .map((row) => ({
          languageCode: row.languageCode,
          isSource: sameLanguageCode(row.languageCode, normalizedSourceLanguageCode),
          title: row.title || '',
          bodyText: row.bodyText || row.translatedText || '',
          translatedText: row.bodyText || row.translatedText || '',
          mapYouAreHereLabel: row.mapYouAreHereLabel || '',
          autoGenerated: Boolean(row.autoGenerated),
          reviewStatus: normalizeReviewStatus(row.reviewStatus, 'draft'),
          freshness: sameLanguageCode(row.languageCode, normalizedSourceLanguageCode) ? 'source' : (row.freshness || 'current'),
          isOutOfDate: sameLanguageCode(row.languageCode, normalizedSourceLanguageCode) ? false : Boolean(row.isOutOfDate),
        })),
      {
        languageCode: normalizedViewLanguageCode,
        isSource: sameLanguageCode(normalizedViewLanguageCode, normalizedSourceLanguageCode),
        title: (title || '').trim(),
        bodyText: currentEditorText,
        translatedText: currentEditorText,
        mapYouAreHereLabel: viewVersion?.mapYouAreHereLabel || '',
        autoGenerated: false,
        reviewStatus: 'draft',
        freshness: sameLanguageCode(normalizedViewLanguageCode, normalizedSourceLanguageCode) ? 'source' : 'current',
        isOutOfDate: false,
      },
    ], normalizedSourceLanguageCode);
    const sourceDraft = normalizedLanguageVersions.find((row) => sameLanguageCode(row.languageCode, normalizedSourceLanguageCode)) || null;
    const sourceTitle = sourceDraft?.title || '';
    const sourceText = sourceDraft?.bodyText || sourceDraft?.translatedText || '';

    return {
      siteId,
      areaId: resolvedAreaId,
      title: sourceTitle,
      englishText: sourceText,
      sourceLanguageCode: normalizedSourceLanguageCode,
      monetisationMode: effectiveMonetisationMode,
      selectedAssetId: selectedAssetId ? Number(selectedAssetId) : null,
      mapId: selectedMapId ? Number(selectedMapId) : null,
      sections: sectionBlocks,
      languageVersions: normalizedLanguageVersions.map((row) => ({
        languageCode: row.languageCode,
        title: row.title || '',
        bodyText: row.bodyText || row.translatedText || '',
        autoGenerated: Boolean(row.autoGenerated),
        reviewStatus: normalizeReviewStatus(row.reviewStatus, 'draft'),
        mapYouAreHereLabel: row.mapYouAreHereLabel || '',
      })),
      translations: normalizedLanguageVersions
        .filter((row) => !sameLanguageCode(row.languageCode, normalizedSourceLanguageCode))
        .map((row) => ({
          languageCode: row.languageCode,
          title: row.title || null,
          translatedText: row.bodyText || row.translatedText || '',
          autoGenerated: Boolean(row.autoGenerated),
          mapYouAreHereLabel: row.mapYouAreHereLabel || null,
      })),
    };
  }, [area, blocks, effectiveMonetisationMode, languageVersions, plaque, selectedAssetId, selectedMapId, siteId, sourceLanguageCode, title, viewLanguageCode, viewVersion]);

  const persistPlaque = useCallback(async ({ showSuccessAlert = true } = {}) => {
    if (!siteId) {
      alert('Select a site first.');
      return null;
    }
    const payload = buildSavePayload();
    if (!payload.title) {
      alert('Title is required.');
      return null;
    }
    const token = localStorage.getItem('token');
    setSaving(true);
    setError('');

    try {
      const res = await fetch(activePlaqueId ? `/api/plaques/${activePlaqueId}` : '/api/plaques', {
        method: activePlaqueId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Save failed (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      const updated = data.plaque || data;
      const resolvedSourceLanguageCode = normalizeLanguageCode(
        updated.sourceLanguageCode || updated.source_language_code || payload.sourceLanguageCode,
        payload.sourceLanguageCode || sourceLanguageCode || 'en'
      );
      const normalizedLanguageVersions = normalizeLanguageVersions({
        plaque: {
          ...updated,
          sourceLanguageCode: resolvedSourceLanguageCode,
          source_language_code: resolvedSourceLanguageCode,
        },
        translations: data.translations || updated.translations || [],
        languageVersions: data.languageVersions || updated.languageVersions || [],
      });
      setPlaque(updated);
      setMonetisationMode(
        normalizeMonetisationMode(
          updated.monetisationMode || updated.monetisation_mode || payload.monetisationMode || site?.defaultMonetisationMode || 'off',
          'off'
        )
      );
      setCurrentRevision(normalizeCurrentRevision(data.currentRevision || updated.currentRevision || null));
      setSourceLanguageCode(resolvedSourceLanguageCode);
      setLanguageVersions(normalizedLanguageVersions);
      loadEditorLanguage(viewLanguageCode, normalizedLanguageVersions, updated);
      if (!activePlaqueId && updated?.id) setActivePlaqueId(updated.id);
      onSaved?.(updated);
      if (showSuccessAlert) alert('Saved.');
      return {
        plaque: updated,
        languageVersions: normalizedLanguageVersions,
        currentRevision: normalizeCurrentRevision(data.currentRevision || updated.currentRevision || null),
      };
    } catch (err) {
      console.error('Failed to save plaque', err);
      setError(err.message || 'Failed to save plaque.');
      return null;
    } finally {
      setSaving(false);
    }
  }, [
    activePlaqueId,
    buildSavePayload,
    onSaved,
    siteId,
    site?.defaultMonetisationMode,
    sourceLanguageCode,
    viewLanguageCode,
    loadEditorLanguage,
  ]);

  const handleSave = async () => {
    await persistPlaque();
  };

  const getErrorMessage = useCallback(async (res, fallback) => {
    const bodyText = await res.text().catch(() => '');
    if (bodyText) {
      try {
        const data = JSON.parse(bodyText);
        if (data?.error) return data.error;
        if (data?.message) return data.message;
      } catch (err) {
        return bodyText;
      }
    }
    return fallback;
  }, []);

  const handleHumanize = async () => {
    const trimmedInstructions = humanizeInstructions.trim();
    const saved = await persistPlaque({ showSuccessAlert: false });
    const savedPlaqueId = saved?.plaque?.id || activePlaqueId;
    if (!savedPlaqueId) {
      alert('Save the plaque first, then humanize it.');
      return;
    }

    const token = localStorage.getItem('token');
    setHumanizing(true);
    setHumanizeMessage('');
    setHumanizeError('');
    setError('');

    try {
      const res = await fetch(`/api/plaques/${savedPlaqueId}/humanize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          instructions: trimmedInstructions,
          includeTranslations: humanizeIncludeTranslations,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.error || data?.message || `Humanize failed (${res.status})`);
      }

      const updated = data?.plaque || {};
      const normalizedLanguageVersions = normalizeLanguageVersions({
        plaque: updated,
        translations: data?.translations || [],
        languageVersions: data?.languageVersions || updated.languageVersions || [],
      });
      const nextRevision = normalizeCurrentRevision(data?.currentRevision || null);
      const providerLabel = formatProviderModel(
        data?.provider || nextRevision?.provider || DEFAULT_KRYTEN_PROVIDER,
        data?.model || nextRevision?.model || DEFAULT_KRYTEN_MODEL,
        'Kryten default model'
      );
      setPlaque(updated);
      setCurrentRevision(nextRevision);
      const nextSourceLanguageCode = normalizeLanguageCode(updated.sourceLanguageCode || updated.source_language_code, 'en');
      setSourceLanguageCode(nextSourceLanguageCode);
      setLanguageVersions(normalizedLanguageVersions);
      loadEditorLanguage(viewLanguageCode, normalizedLanguageVersions, updated);
      setHumanizeMessage(
        humanizeIncludeTranslations
          ? `Kryten humanized the source copy and all saved translations using ${providerLabel}.`
          : `Kryten humanized the source copy using ${providerLabel}.`
      );
      onSaved?.(updated);
    } catch (err) {
      console.error('Humanize failed', err);
      setHumanizeError(err.message || 'Humanize failed.');
    } finally {
      setHumanizing(false);
    }
  };

  const updateLanguageVersion = useCallback((languageCode, patch) => {
    const normalizedLanguageCode = normalizeLanguageCode(languageCode);
    if (!normalizedLanguageCode) return;
    if (sameLanguageCode(normalizedLanguageCode, viewLanguageCode)) {
      if (Object.prototype.hasOwnProperty.call(patch, 'title')) {
        setTitle(patch.title || '');
      }
      if (Object.prototype.hasOwnProperty.call(patch, 'bodyText') || Object.prototype.hasOwnProperty.call(patch, 'translatedText')) {
        const nextBody = patch.bodyText ?? patch.translatedText ?? '';
        setBlocks(parseBlocks(nextBody));
      }
      setLanguageVersions((prev) => sortLanguageVersions(prev.map((row) => (
        sameLanguageCode(row.languageCode, normalizedLanguageCode)
          ? {
              ...row,
              ...patch,
              languageCode: normalizedLanguageCode,
              bodyText: patch.bodyText ?? patch.translatedText ?? row.bodyText ?? row.translatedText ?? '',
              translatedText: patch.bodyText ?? patch.translatedText ?? row.bodyText ?? row.translatedText ?? '',
              isSource: sameLanguageCode(normalizedLanguageCode, sourceLanguageCode),
              autoGenerated: false,
              freshness: sameLanguageCode(normalizedLanguageCode, sourceLanguageCode) ? 'source' : 'current',
              isOutOfDate: sameLanguageCode(normalizedLanguageCode, sourceLanguageCode) ? false : Boolean(row.isOutOfDate),
            }
          : row
      )), normalizeLanguageCode(sourceLanguageCode, 'en')));
      return;
    }

    setLanguageVersions((prev) => {
      const hasRow = prev.some((row) => sameLanguageCode(row.languageCode, normalizedLanguageCode));
      const nextRows = hasRow
        ? prev.map((row) => (
            sameLanguageCode(row.languageCode, normalizedLanguageCode)
              ? {
                  ...row,
                  ...patch,
                  languageCode: normalizedLanguageCode,
                  bodyText: patch.bodyText ?? patch.translatedText ?? row.bodyText ?? row.translatedText ?? '',
                  translatedText: patch.bodyText ?? patch.translatedText ?? row.bodyText ?? row.translatedText ?? '',
                  reviewStatus: normalizeReviewStatus(patch.reviewStatus ?? row.reviewStatus, 'draft'),
                  freshness: patch.freshness || row.freshness || 'current',
                }
              : row
          ))
        : [
            ...prev,
            {
              languageCode: normalizedLanguageCode,
              isSource: false,
              title: patch.title || '',
              bodyText: patch.bodyText ?? patch.translatedText ?? '',
              translatedText: patch.bodyText ?? patch.translatedText ?? '',
              mapYouAreHereLabel: patch.mapYouAreHereLabel || '',
              autoGenerated: Boolean(patch.autoGenerated),
              reviewStatus: normalizeReviewStatus(patch.reviewStatus, 'draft'),
              freshness: patch.freshness || 'current',
              isOutOfDate: Boolean(patch.isOutOfDate),
              sourceContentUpdatedAt: patch.sourceContentUpdatedAt || null,
              contentUpdatedAt: patch.contentUpdatedAt || null,
            },
          ];
      return sortLanguageVersions(nextRows, normalizeLanguageCode(sourceLanguageCode, 'en'));
    });
  }, [sourceLanguageCode, viewLanguageCode]);

  const getLanguageDraft = useCallback((languageCode) => {
    const normalizedLanguageCode = normalizeLanguageCode(languageCode);
    if (!normalizedLanguageCode) return null;
    const row = languageVersionMap.get(normalizedLanguageCode.toLowerCase()) || null;
    const isEditingThisLanguage = sameLanguageCode(normalizedLanguageCode, viewLanguageCode);
    const isSourceLanguage = sameLanguageCode(normalizedLanguageCode, sourceLanguageCode);
    const fallbackTitle = isSourceLanguage ? (plaque?.title || '') : '';
    const fallbackBody = isSourceLanguage ? (plaque?.english_text || plaque?.englishText || '') : '';
    const bodyText = isEditingThisLanguage
      ? sourceBodyText
      : (row?.bodyText || row?.translatedText || fallbackBody || '');
    return {
      languageCode: normalizedLanguageCode,
      isSource: isSourceLanguage,
      title: isEditingThisLanguage ? (title || row?.title || fallbackTitle) : (row?.title || fallbackTitle),
      bodyText,
      translatedText: bodyText,
      mapYouAreHereLabel: row?.mapYouAreHereLabel || '',
      autoGenerated: row?.autoGenerated || false,
      reviewStatus: normalizeReviewStatus(row?.reviewStatus, 'draft'),
      freshness: isSourceLanguage ? 'source' : (row?.freshness || 'current'),
      isOutOfDate: isSourceLanguage ? false : Boolean(row?.isOutOfDate),
      sourceContentUpdatedAt: row?.sourceContentUpdatedAt || null,
      contentUpdatedAt: row?.contentUpdatedAt || null,
    };
  }, [languageVersionMap, plaque, sourceBodyText, sourceLanguageCode, title, viewLanguageCode]);

  const handlePromoteSourceLanguage = useCallback((nextSourceLanguageCode) => {
    const normalizedNextSourceCode = normalizeLanguageCode(nextSourceLanguageCode);
    if (!normalizedNextSourceCode || sameLanguageCode(normalizedNextSourceCode, sourceLanguageCode)) return;
    const nextSourceVersion = getLanguageDraft(normalizedNextSourceCode);
    if (!nextSourceVersion) {
      setSourceLanguageCode(normalizedNextSourceCode);
      setCompareFromCode(normalizedNextSourceCode);
      if (sameLanguageCode(compareToCode, normalizedNextSourceCode)) {
        setCompareToCode(getDefaultCompareToLanguageCode(languageVersions, normalizedNextSourceCode));
      }
      return;
    }

    const previousSourceCode = normalizeLanguageCode(sourceLanguageCode, 'en');
    const previousSourceVersion = getLanguageDraft(previousSourceCode) || {
      languageCode: previousSourceCode,
      isSource: false,
      title: '',
      bodyText: '',
      translatedText: '',
      mapYouAreHereLabel: '',
      autoGenerated: false,
      reviewStatus: 'draft',
      freshness: 'current',
      isOutOfDate: false,
      sourceContentUpdatedAt: null,
      contentUpdatedAt: null,
    };
    const nextRows = sortLanguageVersions([
      {
        ...previousSourceVersion,
        isSource: false,
        autoGenerated: false,
        reviewStatus: 'draft',
        freshness: 'current',
        isOutOfDate: false,
      },
      ...languageVersions.filter((row) => (
        !sameLanguageCode(row.languageCode, previousSourceCode)
        && !sameLanguageCode(row.languageCode, normalizedNextSourceCode)
      )),
      {
        ...nextSourceVersion,
        isSource: true,
        autoGenerated: false,
        reviewStatus: 'draft',
        freshness: 'source',
        isOutOfDate: false,
      },
    ], normalizedNextSourceCode);

    setSourceLanguageCode(normalizedNextSourceCode);
    setLanguageVersions(nextRows);
    setCompareFromCode(normalizedNextSourceCode);
    if (sameLanguageCode(compareToCode, normalizedNextSourceCode)) {
      setCompareToCode(previousSourceCode);
    }
  }, [compareToCode, getLanguageDraft, languageVersions, sourceLanguageCode]);

  const handleTranslate = async () => {
    const saved = await persistPlaque({ showSuccessAlert: false });
    const savedPlaqueId = saved?.plaque?.id || activePlaqueId;
    if (!savedPlaqueId) {
      alert('Save the plaque first, then translate.');
      return;
    }
    if (!compareToCode) {
      alert('Select a target language.');
      return;
    }
    if (!sameLanguageCode(compareFromCode, sourceLanguageCode)) {
      alert(`AI translation currently runs from the current source language (${getLanguageLabel(sourceLanguageCode)}). Use Copy to clone any other comparison pair.`);
      return;
    }
    if (sameLanguageCode(compareToCode, sourceLanguageCode)) {
      alert('Choose a non-source language for the right pane.');
      return;
    }
    const token = localStorage.getItem('token');
    setTranslating(true);
    setError('');
    setTranslationMessage('');

    try {
      const currentSourceText = sameLanguageCode(viewLanguageCode, sourceLanguageCode)
        ? serializePlaqueContent({ blocks })
        : (sourceVersion?.bodyText || sourceVersion?.translatedText || '');
      const currentSourceTitle = sameLanguageCode(viewLanguageCode, sourceLanguageCode)
        ? (title || '')
        : (sourceVersion?.title || '');
      const currentSourceMapLabel = sourceVersion?.mapYouAreHereLabel || '';
      const res = await fetch(`/api/plaques/${savedPlaqueId}/translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sourceLanguageCode,
          sourceTitle: currentSourceTitle,
          sourceText: currentSourceText,
          sourceMapLabel: currentSourceMapLabel,
          languages: [compareToCode],
          maintainTitles: maintainTranslationTitles,
        }),
      });
      if (!res.ok) {
        throw new Error(await getErrorMessage(res, `Translate failed (${res.status})`));
      }
      const data = await res.json().catch(() => ({}));
      const updated = data?.plaque || plaque || {};
      const resolvedSourceLanguageCode = normalizeLanguageCode(
        updated.sourceLanguageCode || updated.source_language_code || sourceLanguageCode,
        sourceLanguageCode || 'en'
      );
      const translatedRows = normalizeLanguageVersions({
        plaque: {
          ...updated,
          sourceLanguageCode: resolvedSourceLanguageCode,
          source_language_code: resolvedSourceLanguageCode,
        },
        translations: data.translations || [],
        languageVersions: data.languageVersions || updated.languageVersions || [],
      });
      setPlaque(updated);
      setSourceLanguageCode(resolvedSourceLanguageCode);
      setLanguageVersions(translatedRows);
      loadEditorLanguage(viewLanguageCode, translatedRows, updated);
      setTranslationMeta({
        provider: data?.provider || 'poly',
        model: data?.model || '',
      });
      setTranslationMessage(
        `Updated ${getLanguageLabel(compareToCode)} with ${formatProviderModel(data?.provider || 'poly', data?.model, 'Poly')}.`
      );
    } catch (err) {
      console.error('Translate failed', err);
      setError(err.message || 'Translate failed.');
    } finally {
      setTranslating(false);
    }
  };

  const handleRemoveTranslation = async (languageCode) => {
    const normalizedLanguageCode = String(languageCode || '').trim();
    if (!normalizedLanguageCode) return;
    const ok = window.confirm(`Remove the ${normalizedLanguageCode} translation?`);
    if (!ok) return;

    if (!activePlaqueId) {
      if (sameLanguageCode(normalizedLanguageCode, sourceLanguageCode)) {
        alert(`The source language (${getLanguageLabel(sourceLanguageCode)}) cannot be removed.`);
        return;
      }
      const nextLanguageVersions = languageVersions.filter((row) => !sameLanguageCode(row.languageCode, normalizedLanguageCode));
      setLanguageVersions(nextLanguageVersions);
      if (sameLanguageCode(viewLanguageCode, normalizedLanguageCode)) {
        loadEditorLanguage(sourceLanguageCode, nextLanguageVersions, plaque);
      }
      if (sameLanguageCode(compareToCode, normalizedLanguageCode)) {
        setCompareToCode(getDefaultCompareToLanguageCode(nextLanguageVersions, sourceLanguageCode));
      }
      setTranslationMessage(`Removed ${normalizedLanguageCode}. Save the plaque to keep the change.`);
      return;
    }

    const token = localStorage.getItem('token');
    setRemovingTranslationCode(normalizedLanguageCode);
    setError('');
    setTranslationMessage('');
    try {
      const saved = await persistPlaque({ showSuccessAlert: false });
      if (!saved) {
        throw new Error('Save failed before removing the translation.');
      }
      const effectivePlaqueId = saved?.plaque?.id || activePlaqueId;
      const effectiveSourceLanguageCode = normalizeLanguageCode(
        saved?.plaque?.sourceLanguageCode || saved?.plaque?.source_language_code || sourceLanguageCode,
        sourceLanguageCode || 'en'
      );
      if (sameLanguageCode(normalizedLanguageCode, effectiveSourceLanguageCode)) {
        alert(`The source language (${getLanguageLabel(effectiveSourceLanguageCode)}) cannot be removed.`);
        return;
      }
      const res = await fetch(`/api/plaques/${effectivePlaqueId}/translations/${encodeURIComponent(normalizedLanguageCode)}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        throw new Error(await getErrorMessage(res, `Remove failed (${res.status})`));
      }
      const data = await res.json().catch(() => ({}));
      const updated = data?.plaque || plaque || {};
      const normalizedLanguageVersions = normalizeLanguageVersions({
        plaque: updated,
        translations: data?.translations || [],
        languageVersions: data?.languageVersions || updated.languageVersions || [],
      });
      const resolvedSourceLanguageCode = normalizeLanguageCode(
        updated.sourceLanguageCode || updated.source_language_code || effectiveSourceLanguageCode,
        effectiveSourceLanguageCode
      );
      setPlaque(updated);
      setSourceLanguageCode(resolvedSourceLanguageCode);
      setLanguageVersions(normalizedLanguageVersions);
      if (sameLanguageCode(viewLanguageCode, normalizedLanguageCode)) {
        loadEditorLanguage(resolvedSourceLanguageCode, normalizedLanguageVersions, updated);
      } else {
        loadEditorLanguage(viewLanguageCode, normalizedLanguageVersions, updated);
      }
      if (data?.currentRevision) {
        setCurrentRevision(normalizeCurrentRevision(data.currentRevision));
      }
      if (sameLanguageCode(compareToCode, normalizedLanguageCode)) {
        setCompareToCode(getDefaultCompareToLanguageCode(normalizedLanguageVersions, resolvedSourceLanguageCode));
      }
      setTranslationMessage(`Removed ${normalizedLanguageCode}.`);
    } catch (err) {
      console.error('Remove translation failed', err);
      setError(err.message || 'Failed to remove translation.');
    } finally {
      setRemovingTranslationCode('');
    }
  };

  const handleCopyLeftToRight = useCallback(() => {
    const sourceRow = compareFromVersion || null;
    const targetCode = normalizeLanguageCode(compareToCode);
    if (!sourceRow || !targetCode) return;
    updateLanguageVersion(targetCode, {
      title: sourceRow.title || '',
      bodyText: sourceRow.bodyText || sourceRow.translatedText || '',
      mapYouAreHereLabel: sourceRow.mapYouAreHereLabel || '',
      autoGenerated: false,
      reviewStatus: 'draft',
      freshness: sameLanguageCode(targetCode, sourceLanguageCode) ? 'source' : 'current',
      isOutOfDate: false,
    });
    setTranslationMessage(
      `Copied ${getLanguageLabel(compareFromCode)} into ${getLanguageLabel(compareToCode)}.`
    );
  }, [compareFromCode, compareFromVersion, compareToCode, sourceLanguageCode, updateLanguageVersion]);

  const handleSetReviewStatus = useCallback((languageCode, reviewStatus) => {
    const normalizedLanguageCode = normalizeLanguageCode(languageCode);
    if (!normalizedLanguageCode || sameLanguageCode(normalizedLanguageCode, sourceLanguageCode)) return;
    updateLanguageVersion(normalizedLanguageCode, {
      reviewStatus: normalizeReviewStatus(reviewStatus, 'draft'),
      autoGenerated: false,
    });
    setTranslationMessage(`Marked ${getLanguageLabel(normalizedLanguageCode)} as ${normalizeReviewStatus(reviewStatus, 'draft')}. Save to persist.`);
  }, [sourceLanguageCode, updateLanguageVersion]);

  const handleGenerateCopy = async () => {
    const trimmed = (copyPrompt || '').trim();
    const paragraphsRequested = Math.max(1, Math.min(10, Number(copyParagraphs) || 3));
    if (!trimmed) {
      alert('Enter what you want to generate copy about.');
      return;
    }
    const ok = blocks.some((b) => b.type === 'text' && (b.text || '').trim())
      ? window.confirm('Replace current text blocks with generated copy?')
      : true;
    if (!ok) return;

    const token = localStorage.getItem('token');
    setGeneratingCopy(true);
    setError('');
    try {
      const siteName = site?.name || 'this site';
      const businessDescription = site?.business_description || site?.businessDescription || '';
      const address = site?.address || '';

      const userPrompt = [
        `You are creating an information panel for a point of interest at the ${siteName},`,
        `${businessDescription ? `a ${businessDescription}` : 'a venue'} located in ${address || 'Vietnam'}.`,
        `I want you to create a short title and ${paragraphsRequested} paragraphs of text describing the site or attraction the user entered below using any extra information they have added or requested into your responses.`,
        '',
        'User input:',
        trimmed,
        '',
        'Output requirements:',
        '- Return JSON only',
        '- JSON schema: { "title": string, "paragraphs": string[] }',
        `- "paragraphs" must contain exactly ${paragraphsRequested} strings`,
        '- Each paragraph should be plain text (no markdown headings, no bullet lists)',
      ].join('\n');

      const res = await fetch('/api/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          temperature: 0.6,
          max_tokens: 900,
          messages: [
            { role: 'system', content: 'You write clear, accurate informational copy for visitors.' },
            { role: 'user', content: userPrompt },
          ],
        }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(body || `Generate copy failed (${res.status})`);
      }
      const data = await res.json().catch(() => ({}));
      const text = data?.choices?.[0]?.message?.content || '';
      let generatedTitle = '';
      let paragraphs = [];
      try {
        const parsed = JSON.parse(String(text).trim());
        generatedTitle = typeof parsed?.title === 'string' ? parsed.title.trim() : '';
        paragraphs = Array.isArray(parsed?.paragraphs) ? parsed.paragraphs.map((p) => String(p || '').trim()) : [];
      } catch (err) {
        generatedTitle = '';
        paragraphs = String(text)
          .split(/\r?\n\s*\r?\n/)
          .map((p) => p.trim())
          .filter(Boolean);
      }

      const normalized = paragraphs.filter(Boolean).slice(0, paragraphsRequested);
      while (normalized.length < paragraphsRequested) normalized.push('');
        setBlocks(normalizeBlocks(normalized.map((p) => ({ type: 'text', text: p }))));

      if (generatedTitle) {
        const shouldReplaceTitle = !(title || '').trim() || window.confirm('Replace the existing title with the generated title?');
        if (shouldReplaceTitle) setTitle(generatedTitle);
      }
    } catch (err) {
      console.error('Generate copy failed', err);
      setError(err.message || 'Generate copy failed.');
    } finally {
      setGeneratingCopy(false);
    }
  };

  const copyQrToClipboard = async () => {
    if (!qrDataUrl) return;
    try {
      const resp = await fetch(qrDataUrl);
      const blob = await resp.blob();
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({ [blob.type]: blob })]);
        alert('QR code copied to clipboard.');
        return;
      }
      throw new Error('Clipboard image copy is not supported in this browser.');
    } catch (err) {
      console.warn('Failed to copy QR to clipboard', err);
      if (landingUrl) {
        try {
          await navigator.clipboard.writeText(landingUrl);
          alert('QR image copy not supported here. Landing URL copied instead.');
        } catch (inner) {
          window.prompt('Copy landing URL:', landingUrl);
        }
      } else {
        alert(err.message || 'Failed to copy QR code.');
      }
    }
  };

  const goToAdjacentPlaque = useCallback((direction) => {
    if (!hasPlaqueNavigation) return;
    const nextIndex = (currentPlaqueIndex + direction + navigablePlaques.length) % navigablePlaques.length;
    const nextPlaque = navigablePlaques[nextIndex];
    if (!nextPlaque?.id) return;
    setError('');
    setTranslationMessage('');
    setHumanizeMessage('');
    setHumanizeError('');
    setActivePlaqueId(nextPlaque.id);
  }, [
    currentPlaqueIndex,
    hasPlaqueNavigation,
    navigablePlaques,
  ]);

  const openLandingPage = useCallback(() => {
    if (!previewLandingUrl || typeof window === 'undefined') return;
    window.open(previewLandingUrl, '_blank', 'noopener,noreferrer');
  }, [previewLandingUrl]);

  const refreshPromotionPreview = useCallback(() => {
    setPromotionPreviewRefreshToken((value) => value + 1);
  }, []);

  const toggleMainPanel = useCallback((panelKey) => {
    setMainPanelsOpen((prev) => ({
      ...prev,
      [panelKey]: !prev[panelKey],
    }));
  }, []);

  const renderMainPanel = useCallback((panelKey, titleLabel, children) => (
    <div className="rounded-lg border border-gray-800 bg-gray-950/70">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
        onClick={() => toggleMainPanel(panelKey)}
      >
        <span className="text-base font-semibold text-white">{titleLabel}</span>
        <ChevronRight
          size={18}
          className={`shrink-0 text-gray-300 transition-transform ${mainPanelsOpen[panelKey] ? 'rotate-90' : ''}`}
        />
      </button>
      {mainPanelsOpen[panelKey] && (
        <div className="border-t border-gray-800 p-4 space-y-4">
          {children}
        </div>
      )}
    </div>
  ), [mainPanelsOpen, toggleMainPanel]);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black bg-opacity-80 p-4" onClick={close}>
      <div
        className="relative mx-auto flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-lg bg-gray-950"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="z-10 shrink-0 border-b border-gray-800 bg-gray-950 p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                {hasPlaqueNavigation && (
                  <>
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded bg-gray-800 text-white hover:bg-gray-700"
                      onClick={() => goToAdjacentPlaque(-1)}
                      disabled={loading || saving || translating || humanizing}
                      title="Previous plaque"
                    >
                      <ChevronLeft size={18} />
                    </button>
                    <button
                      type="button"
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded bg-gray-800 text-white hover:bg-gray-700"
                      onClick={() => goToAdjacentPlaque(1)}
                      disabled={loading || saving || translating || humanizing}
                      title="Next plaque"
                    >
                      <ChevronRight size={18} />
                    </button>
                  </>
                )}
                <div className="text-lg font-semibold truncate">{activePlaqueId ? 'Edit Plaque' : 'New Plaque'}</div>
              </div>
              {landingUrl && (
                <div className="text-xs text-gray-400 truncate">{landingUrl}</div>
              )}
              {normalizedCurrentRevision && (
                <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                  <span className={`inline-flex rounded-full px-2 py-1 font-semibold ${
                    normalizedCurrentRevision.revisionKind === 'kryten'
                      ? 'bg-cyan-900/70 text-cyan-200'
                      : normalizedCurrentRevision.revisionKind === 'nina'
                        ? 'bg-indigo-900/70 text-indigo-200'
                      : 'bg-gray-800 text-gray-200'
                  }`}>
                    Version in use: {getRevisionKindLabel(normalizedCurrentRevision.revisionKind)}
                  </span>
                  <span className="text-gray-400">{currentRevisionSummary}</span>
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2">
                <label className="hidden md:flex items-center gap-2 text-xs text-gray-300">
                  <span>View</span>
                  <select
                    className="rounded bg-gray-800 border border-gray-700 px-2 py-1 text-white"
                    value={viewLanguageCode}
                    onChange={(e) => loadEditorLanguage(e.target.value, languageVersions, plaque)}
                    disabled={saving || translating || humanizing}
                  >
                    {allLanguageCodes.map((code) => (
                      <option key={`source-${code}`} value={code}>
                        {getLanguageLabel(code)}
                      </option>
                    ))}
                  </select>
                </label>
                <button
                  className="px-3 py-1 bg-gray-700 rounded text-white"
                  onClick={close}
                  disabled={saving || translating || humanizing}
                >
                  Close
                </button>
                <button
                  className="px-3 py-1 bg-indigo-600 rounded text-white"
                  onClick={handleSave}
                  disabled={saving || translating || humanizing}
                >
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  className="px-2 py-1 bg-gray-700 rounded text-xs text-white disabled:bg-gray-800 disabled:text-gray-500"
                  onClick={openLandingPage}
                  disabled={!previewLandingUrl}
                >
                  Open Landing Page
                </button>
                <button className="px-2 py-1 bg-gray-700 rounded text-xs text-white" onClick={addTextBlock}>
                  Add Text
                </button>
                <button className="px-2 py-1 bg-gray-700 rounded text-xs text-white" onClick={addSubtitleBlock}>
                  Add Subtitle
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-hidden p-4">
          {error && <div className="mb-3 text-red-300 text-sm">{error}</div>}
          {loading && <div className="text-gray-300">Loading…</div>}

          {!loading && (
            <div className="flex h-full min-h-0 flex-col gap-4 lg:flex-row">
              <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain pr-2">
                <div className="space-y-4 min-w-0">
                {renderMainPanel('generateCopy', 'Generate Copy', (
                  <>
                <div className="bg-gray-900 rounded p-4">
                  <div className="text-lg font-semibold mb-2">Generate Copy</div>
                  <div className="text-sm text-gray-400 mb-3">
                    Enter the attraction name and any info you want included. Generates paragraphs into text blocks.
                  </div>
                  <textarea
                    className="w-full min-h-24 px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                    value={copyPrompt}
                    onChange={(e) => setCopyPrompt(e.target.value)}
                    placeholder="e.g. The ancient temple ruins, built in 1200s. Mention opening hours, cultural significance, and respectful visitor behavior…"
                  />
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <label className="text-sm text-gray-200 flex items-center gap-2">
                      Paragraphs
                      <input
                        type="number"
                        min={1}
                        max={10}
                        className="w-20 px-2 py-1 rounded bg-gray-800 text-white border border-gray-700"
                        value={copyParagraphs}
                        onChange={(e) => setCopyParagraphs(e.target.value)}
                      />
                    </label>
                    <button
                      className="px-3 py-1 rounded bg-indigo-600 text-white"
                      onClick={handleGenerateCopy}
                      disabled={generatingCopy || saving || translating || humanizing}
                    >
                      {generatingCopy ? 'Generating…' : 'Generate Copy'}
                    </button>
                    <button
                      className="px-3 py-1 rounded bg-cyan-700 text-white disabled:bg-cyan-950 disabled:text-cyan-200/60"
                      onClick={() => setHumanizePanelOpen((prev) => !prev)}
                      disabled={generatingCopy || saving || translating || humanizing || !siteId}
                    >
                      {humanizePanelOpen ? 'Hide Humanize' : 'Humanize'}
                    </button>
                  </div>
                  {humanizePanelOpen && (
                    <div className="mt-4 rounded border border-cyan-800/60 bg-cyan-950/20 p-4">
                      <div className="text-sm font-semibold text-cyan-100">Kryten Humanizer</div>
                      <div className="mt-1 text-xs text-cyan-100/75">
                        Save-first workflow. Kryten humanizes the current source draft and can optionally process each saved translation one at a time.
                      </div>
                      <div className="mt-2 text-xs text-cyan-100/75">
                        Model in use: the saved AI Agents Kryten setting, or the built-in default if no global setting has been saved yet.
                      </div>
                      <textarea
                        className="mt-3 w-full min-h-24 px-3 py-2 rounded bg-gray-800 text-white border border-cyan-800/70"
                        value={humanizeInstructions}
                        onChange={(e) => setHumanizeInstructions(e.target.value)}
                        placeholder="Optional extra instructions, e.g. Make the content longer."
                      />
                      <label className="mt-3 flex items-center gap-2 text-sm text-gray-200">
                        <input
                          type="checkbox"
                          checked={humanizeIncludeTranslations}
                          onChange={(e) => setHumanizeIncludeTranslations(e.target.checked)}
                        />
                        Include Translations
                      </label>
                      {!activePlaqueId && (
                        <div className="mt-2 text-xs text-amber-300">
                          Save the plaque once before running Kryten so the version can be recorded and pushed to the landing page.
                        </div>
                      )}
                      {humanizeError && (
                        <div className="mt-3 text-sm text-red-300">{humanizeError}</div>
                      )}
                      {humanizeMessage && (
                        <div className="mt-3 text-sm text-cyan-200">{humanizeMessage}</div>
                      )}
                      <div className="mt-3 flex flex-wrap items-center gap-3">
                        <button
                          className="px-3 py-1 rounded bg-cyan-700 text-white disabled:bg-cyan-950 disabled:text-cyan-200/60"
                          onClick={handleHumanize}
                          disabled={saving || translating || generatingCopy || humanizing}
                        >
                          {humanizing ? 'Humanizing…' : 'Go!'}
                        </button>
                        {normalizedCurrentRevision && (
                          <span className="text-xs text-gray-400">
                            Current version: {getRevisionKindLabel(normalizedCurrentRevision.revisionKind)} · {currentRevisionSummary}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-gray-950 rounded p-4 border border-gray-800">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <div>
                      <div className="text-lg font-semibold">Sections</div>
                      <div className="text-xs text-gray-400">
                        Add jump points and drag them into position using the block order below.
                      </div>
                    </div>
                  </div>

                  {!activePlaqueId && (
                    <div className="text-sm text-gray-400 mb-3">
                      Save the plaque first to enable sections.
                    </div>
                  )}

                  {getSectionBlocks().length > 0 && (
                    <div className="space-y-2 mb-3">
                      {getSectionBlocks().map(({ block, index }, idx, arr) => (
                        <div key={block.id || `section-${index}`} className="flex flex-col md:flex-row md:items-center gap-2 bg-gray-900/60 border border-gray-800 rounded px-3 py-2">
                          <div className="flex-1 min-w-0">
                            <input
                              className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                              value={block.title || ''}
                              onChange={(e) => updateBlock(index, { title: e.target.value })}
                              placeholder="Section name"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                              onClick={() => moveBlock(index, -1)}
                              disabled={index === 0}
                            >
                              Up
                            </button>
                            <button
                              className="px-2 py-1 bg-gray-700 text-xs rounded text-white disabled:bg-gray-800 disabled:text-gray-500"
                              onClick={() => moveBlock(index, 1)}
                              disabled={index === blocks.length - 1}
                            >
                              Down
                            </button>
                            <button
                              className="px-2 py-1 bg-red-700 text-xs rounded text-white"
                              onClick={() => removeBlock(index)}
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {getSectionBlocks().length === 0 && activePlaqueId && (
                    <div className="text-sm text-gray-400 mb-3">
                      No sections yet. Add one to create a jump point.
                    </div>
                  )}

                  <div className="flex flex-col md:flex-row gap-2">
                    <input
                      className="flex-1 px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                      value={sectionName}
                      onChange={(e) => setSectionName(e.target.value)}
                      placeholder="Add a new section (e.g. History)"
                      disabled={!activePlaqueId}
                    />
                    <button
                      className="px-3 py-2 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                      onClick={addSection}
                      disabled={!activePlaqueId || !sectionName.trim()}
                    >
                      Add Section
                    </button>
                  </div>
                </div>
                  </>
                ))}

                {renderMainPanel('landingContent', 'Landing Page Content', (
                  <>
                {!isActivityHubSurface && (
                  <div className="bg-gray-900 rounded p-4">
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <label className="block text-xs text-gray-300">
                        Monetisation Mode
                        <select
                          className="mt-1 w-full rounded bg-gray-800 px-3 py-2 text-white border border-gray-700 disabled:bg-gray-900 disabled:text-gray-500"
                          value={effectiveMonetisationMode}
                          onChange={(e) => setMonetisationMode(e.target.value)}
                          disabled={monetisationIsEnforced}
                        >
                          {MONETISATION_MODE_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <div className="mt-1 text-xs text-gray-500">
                          {monetisationIsEnforced
                            ? 'Free-tier sites always use Full monetisation.'
                            : MONETISATION_MODE_OPTIONS.find((option) => option.value === effectiveMonetisationMode)?.description}
                        </div>
                      </label>
                      <div className="rounded border border-gray-800 bg-gray-950/70 px-3 py-3 text-sm text-gray-300">
                        <div className="text-xs uppercase tracking-wide text-gray-500">Site Default</div>
                        <div className="mt-1 font-semibold text-white">
                          {MONETISATION_MODE_OPTIONS.find((option) => (
                            option.value === normalizeMonetisationMode(site?.defaultMonetisationMode || site?.default_monetisation_mode || 'off', 'off')
                          ))?.label || 'Off'}
                        </div>
                        <div className="mt-1 text-xs text-gray-500">
                          Subscription: {subscriptionType}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-gray-900 rounded p-4">
                  <label className="block text-xs text-gray-300 mb-1">
                    Title ({getLanguageLabel(viewLanguageCode)} view)
                  </label>
                  <input
                    className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                </div>

                <div className="bg-gray-900 rounded p-4">
                  <div className="text-lg font-semibold mb-2">
                    Landing Page Content ({getLanguageLabel(viewLanguageCode)} view)
                  </div>

                  <div ref={blocksRef} className="space-y-3">
                    {blocks.map((block, idx) => (
                      <div
                        key={block.id || `${block.type}-${idx}`}
                        data-block-index={idx}
                        className={`border rounded p-3 ${getBlockCardClasses(block.type)}`}
                      >
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <div className={`text-xs uppercase tracking-wide ${getBlockLabelClasses(block.type)}`}>
                            {block.type === 'image'
                              ? 'Image'
                              : block.type === 'subtitle'
                                ? `Subtitle (${String(block.size || 'h2').toUpperCase()})`
                                : block.type === 'section'
                                  ? 'Section'
                                  : block.type === 'ad'
                                    ? 'Ad'
                                    : block.type === 'campaign'
                                      ? 'Campaign Ad'
                                      : block.type === 'campaign_dynamic'
                                        ? 'Dynamic Ad'
                                      : block.type === 'widget'
                                        ? 'Widget'
                                        : 'Text'} block #{idx + 1}
                            {block.type === 'section' && block.title ? ` — ${block.title}` : ''}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              className="px-2 py-1 bg-gray-800 rounded text-xs"
                              onClick={() => moveBlock(idx, -1)}
                              disabled={idx === 0}
                            >
                              Up
                            </button>
                            <button
                              className="px-2 py-1 bg-gray-800 rounded text-xs"
                              onClick={() => moveBlock(idx, 1)}
                              disabled={idx === blocks.length - 1}
                            >
                              Down
                            </button>
                            <button
                              className="px-2 py-1 bg-gray-800 rounded text-xs"
                              onClick={() => moveBlock(idx, -5)}
                              disabled={idx === 0}
                            >
                              Up 5x
                            </button>
                            <button
                              className="px-2 py-1 bg-gray-800 rounded text-xs"
                              onClick={() => moveBlock(idx, 5)}
                              disabled={idx === blocks.length - 1}
                            >
                              Down 5x
                            </button>
                            <button
                              className="px-2 py-1 bg-gray-800 rounded text-xs"
                              onClick={() => moveBlockTo(idx, 0)}
                              disabled={idx === 0}
                            >
                              Top
                            </button>
                            <button
                              className="px-2 py-1 bg-gray-800 rounded text-xs"
                              onClick={() => moveBlockTo(idx, blocks.length - 1)}
                              disabled={idx === blocks.length - 1}
                            >
                              Bottom
                            </button>
                            <button
                              className="px-2 py-1 bg-red-800 rounded text-xs"
                              onClick={() => removeBlock(idx)}
                              disabled={blocks.length === 1}
                            >
                              Remove
                            </button>
                          </div>
                        </div>

                        {block.type === 'text' && (
                          <>
                            <textarea
                              className={`w-full min-h-32 px-3 py-2 rounded text-white border ${getBlockFieldClasses('text')}`}
                              value={block.text}
                              onChange={(e) => updateBlockText(idx, e.target.value)}
                              placeholder="Enter source text…"
                            />
                            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                              <label className="text-xs text-gray-300">
                                Link title
                                <input
                                  className={`mt-1 w-full px-2 py-1 rounded text-white border ${getBlockFieldClasses('text')}`}
                                  value={block.linkTitle || ''}
                                  onChange={(e) => updateBlock(idx, { linkTitle: e.target.value })}
                                  placeholder="e.g. Learn more"
                                />
                              </label>
                              <label className="text-xs text-gray-300">
                                Link URL
                                <input
                                  className={`mt-1 w-full px-2 py-1 rounded text-white border ${getBlockFieldClasses('text')}`}
                                  value={block.linkUrl || ''}
                                  onChange={(e) => updateBlock(idx, { linkUrl: e.target.value })}
                                  placeholder="https://example.com"
                                />
                              </label>
                            </div>
                          </>
                        )}

                        {block.type === 'image' && (
                          <div className="text-sm text-gray-200">
                            <div className="text-xs text-gray-400 mb-2">Asset ID: {block.assetId}</div>
                            <SelectedAssetPreview
                              asset={imageAssets.find((a) => String(a.id) === String(block.assetId)) || { id: block.assetId }}
                              maxHeight={260}
                            />
                            <label className="mt-3 block text-xs text-gray-300">
                              SEO alt text
                              <input
                                className={`mt-1 w-full px-2 py-1 rounded text-white border ${getBlockFieldClasses('image')}`}
                                value={block.alt || ''}
                                onChange={(e) => updateBlock(idx, { alt: e.target.value })}
                                placeholder="Describe what is shown in the image"
                              />
                            </label>
                            <label className="mt-2 block text-xs text-gray-300">
                              Caption / source notes (not shown on page)
                              <input
                                className={`mt-1 w-full px-2 py-1 rounded text-white border ${getBlockFieldClasses('image')}`}
                                value={block.caption || ''}
                                onChange={(e) => updateBlock(idx, { caption: e.target.value })}
                                placeholder="Optional internal note or source tags"
                              />
                            </label>
                            <label className="mt-2 block text-xs text-gray-300">
                              Tap URL (optional)
                              <input
                                className={`mt-1 w-full px-2 py-1 rounded text-white border ${getBlockFieldClasses('image')}`}
                                value={block.linkUrl || ''}
                                onChange={(e) => updateBlock(idx, { linkUrl: e.target.value })}
                                placeholder="https://example.com"
                              />
                            </label>
                          </div>
                        )}

                        {block.type === 'subtitle' && (
                          <div className="space-y-2">
                            <label className="text-xs text-gray-300">
                              Subtitle text (single sentence)
                              <input
                                className="mt-1 w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800"
                                value={block.text}
                                onChange={(e) => updateBlock(idx, { text: e.target.value })}
                                placeholder="Enter subtitle…"
                              />
                            </label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <label className="text-xs text-gray-300">
                                Size
                                <select
                                  className="mt-1 w-full px-2 py-1 rounded bg-gray-900 text-white border border-gray-800"
                                  value={block.size || 'h2'}
                                  onChange={(e) => updateBlock(idx, { size: e.target.value })}
                                >
                                  <option value="h1">Heading 1</option>
                                  <option value="h2">Heading 2</option>
                                  <option value="h3">Heading 3</option>
                                </select>
                              </label>
                              <label className="text-xs text-gray-300">
                                Link URL (optional)
                                <input
                                  className="mt-1 w-full px-2 py-1 rounded bg-gray-900 text-white border border-gray-800"
                                  value={block.linkUrl || ''}
                                  onChange={(e) => updateBlock(idx, { linkUrl: e.target.value })}
                                  placeholder="https://example.com"
                                />
                              </label>
                            </div>
                          </div>
                        )}
                        {block.type === 'section' && (
                          <div className="space-y-2">
                            <label className="text-xs text-gray-300">
                              Section title
                              <input
                                className={`mt-1 w-full px-3 py-2 rounded text-white border ${getBlockFieldClasses('section')}`}
                                value={block.title || ''}
                                onChange={(e) => updateBlock(idx, { title: e.target.value })}
                                placeholder="Section title…"
                              />
                            </label>
                            <div className="text-xs text-gray-400">
                              This creates a jump point on the landing page.
                            </div>
                          </div>
                        )}
                        {block.type === 'ad' && (
                          <div className="space-y-2 text-sm text-gray-200">
                            <div className="text-xs uppercase tracking-wide text-gray-500">Ad Block</div>
                            <div className="text-xs text-gray-400">
                              Size: {block.size === 'standard' ? 'Standard Banner (320×50)'
                                : block.size === 'large'
                                  ? 'Large Banner (320×100)'
                                  : 'Inline (300×250)'}
                            </div>
                            {block.adId ? (
                              <div className="space-y-1">
                                <div className="font-semibold">
                                  {adsById.get(String(block.adId))?.title || `Ad #${block.adId}`}
                                </div>
                                <div className="text-xs text-gray-400">
                                  Status: {adsById.get(String(block.adId))?.status || 'unknown'}
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400">No ad selected.</div>
                            )}
                            {block.adId ? (
                              (() => {
                                const ad = adsById.get(String(block.adId));
                                if (!ad) return null;
                                const imageId = block.size === 'standard'
                                  ? ad.image_id_standard
                                  : block.size === 'large'
                                    ? ad.image_id_large
                                    : ad.image_id;
                                const asset = imageId ? assetById.get(String(imageId)) : null;
                                return (
                                  <div className="space-y-2">
                                    {asset ? (
                                      <SelectedAssetPreview asset={asset} maxHeight={180} />
                                    ) : null}
                                    {ad.body ? (
                                      <div
                                        className="text-xs text-gray-300 line-clamp-3"
                                        dangerouslySetInnerHTML={{ __html: ad.body }}
                                      />
                                    ) : null}
                                  </div>
                                );
                              })()
                            ) : null}
                            <label className="text-xs text-gray-300">
                              Stick to
                              <select
                                className="mt-1 w-full px-2 py-1 rounded bg-gray-900 text-white border border-gray-800"
                                value={block.size === 'standard'
                                  ? (block.stick || '')
                                  : (block.stick === 'bottom' ? 'bottom' : '')}
                                onChange={(e) => updateBlock(idx, { stick: e.target.value })}
                              >
                                <option value="">None</option>
                                {block.size === 'standard' && <option value="top">Top (nav)</option>}
                                <option value="bottom">Bottom (sticky)</option>
                              </select>
                            </label>
                            {block.size === 'inline' && (
                              <label className="text-xs text-gray-300 flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={block.showText !== false}
                                  onChange={(e) => updateBlock(idx, { showText: e.target.checked })}
                                />
                                Show Text
                              </label>
                            )}
                            <div className="flex flex-wrap gap-2">
                              {selectedAdId && selectedAdId !== block.adId ? (
                                <button
                                  className="px-2 py-1 bg-gray-800 rounded text-xs"
                                  onClick={() => updateBlock(idx, { adId: selectedAdId })}
                                >
                                  Replace with Selected
                                </button>
                              ) : null}
                              {block.adId ? (
                                <button
                                  className="px-2 py-1 bg-gray-800 rounded text-xs"
                                  onClick={() => updateBlock(idx, { adId: null })}
                                >
                                  Clear
                                </button>
                              ) : null}
                            </div>
                          </div>
                        )}
                        {block.type === 'campaign' && (
                          <div className="space-y-3 text-sm text-gray-200">
                            <div className="text-xs uppercase tracking-wide text-gray-500">Campaign Ad Block</div>
                            {block.campaignId ? (
                              (() => {
                                const campaignAd = campaignAdsById.get(String(block.campaignId)) || null;
                                if (!campaignAd) {
                                  return (
                                    <div className={`rounded border p-3 text-xs text-gray-300 ${getBlockFieldClasses('campaign')}`}>
                                      Campaign #{block.campaignId} is not available in the current library response.
                                    </div>
                                  );
                                }
                                return (
                                  <div className="space-y-3">
                                    <div className="overflow-hidden rounded border border-gray-800 bg-gray-950">
                                      {campaignAd.previewImageUrl ? (
                                        <img
                                          src={campaignAd.previewImageUrl}
                                          alt={campaignAd.title || `Campaign #${campaignAd.campaignId}`}
                                          className="h-40 w-full object-cover"
                                          loading="lazy"
                                        />
                                      ) : (
                                        <div className="flex h-40 items-center justify-center text-sm text-gray-500">
                                          No artwork available.
                                        </div>
                                      )}
                                    </div>
                                    <div className="space-y-1">
                                      <div className="font-semibold">
                                        {campaignAd.title || `Campaign #${campaignAd.campaignId}`}
                                      </div>
                                      <div className="text-xs text-gray-400">
                                        {campaignAd.siteName || 'Unknown site'}
                                      </div>
                                      <div className="text-xs text-gray-400">
                                        Target: {campaignAd.landingKey ? `/lp/c/${campaignAd.landingKey}` : 'No landing page'}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()
                            ) : (
                              <div className="text-xs text-gray-400">No campaign selected.</div>
                            )}
                            <div className="flex flex-wrap gap-2">
                              {selectedCampaignAdId && selectedCampaignAdId !== block.campaignId ? (
                                <button
                                  className="px-2 py-1 bg-gray-800 rounded text-xs"
                                  onClick={() => updateBlock(idx, { campaignId: selectedCampaignAdId })}
                                >
                                  Replace with Selected
                                </button>
                              ) : null}
                            </div>
                          </div>
                        )}
                        {block.type === 'campaign_dynamic' && (
                          <div className="space-y-3 text-sm text-gray-200">
                            <div className="text-xs uppercase tracking-wide text-gray-500">Dynamic Ad Block</div>
                            <div className="text-xs text-gray-400">
                              Rotates the selected campaigns in simple round-robin order on the public landing page.
                            </div>
                            {Array.isArray(block.campaignIds) && block.campaignIds.length ? (
                              <div className="space-y-2">
                                {block.campaignIds.map((campaignId, dynamicIndex) => {
                                  const campaignAd = campaignAdsById.get(String(campaignId)) || null;
                                  return (
                                    <div
                                      key={`${block.id || idx}-dynamic-campaign-${campaignId}-${dynamicIndex}`}
                                      className={`rounded border p-3 ${getBlockFieldClasses('campaign_dynamic')}`}
                                    >
                                      <div className="flex items-start gap-3">
                                        <div className="h-14 w-14 flex-none overflow-hidden rounded border border-gray-800 bg-gray-900">
                                          {campaignAd?.previewImageUrl ? (
                                            <img
                                              src={campaignAd.previewImageUrl}
                                              alt={campaignAd.title || `Campaign #${campaignId}`}
                                              className="h-full w-full object-cover"
                                              loading="lazy"
                                            />
                                          ) : (
                                            <div className="flex h-full items-center justify-center text-[10px] text-gray-500">
                                              No art
                                            </div>
                                          )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                          <div className="font-semibold">
                                            {campaignAd?.title || `Campaign #${campaignId}`}
                                          </div>
                                          <div className="text-xs text-gray-400">
                                            {campaignAd?.siteName || 'Unknown site'}
                                          </div>
                                          <div className="text-xs text-gray-400">
                                            Position {dynamicIndex + 1}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400">No campaigns selected.</div>
                            )}
                            <div className="flex flex-wrap gap-2">
                              {campaignAdsDynamicMode && selectedCampaignAdIds.length ? (
                                <button
                                  className="px-2 py-1 bg-gray-800 rounded text-xs"
                                  onClick={() => updateBlock(idx, { campaignIds: selectedCampaignAdIds })}
                                >
                                  Replace with Selected Set
                                </button>
                              ) : null}
                            </div>
                          </div>
                        )}
                        {block.type === 'widget' && (
                          <div className="space-y-2 text-sm text-gray-200">
                            <div className="text-xs uppercase tracking-wide text-gray-500">Widget Block</div>
                            {block.widgetId ? (
                              <div className="space-y-1">
                                <div className="font-semibold">
                                  {widgetsById.get(String(block.widgetId))?.title || `Widget #${block.widgetId}`}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {widgetsById.get(String(block.widgetId))?.partner || 'Unknown partner'}
                                  {widgetsById.get(String(block.widgetId))?.widget_type
                                    ? ` · ${widgetsById.get(String(block.widgetId))?.widget_type}`
                                    : ''}
                                </div>
                                <div className="text-xs text-gray-400">
                                  Status: {widgetsById.get(String(block.widgetId))?.status || 'unknown'}
                                </div>
                              </div>
                            ) : (
                              <div className="text-xs text-gray-400">No widget selected.</div>
                            )}
                            <div className="flex flex-wrap gap-2">
                              {isAdmin && selectedWidgetId && selectedWidgetId !== block.widgetId ? (
                                <button
                                  className="px-2 py-1 bg-gray-800 rounded text-xs"
                                  onClick={() => updateBlock(idx, { widgetId: selectedWidgetId })}
                                >
                                  Replace with Selected
                                </button>
                              ) : null}
                              {isAdmin && block.widgetId ? (
                                <button
                                  className="px-2 py-1 bg-indigo-700 rounded text-xs"
                                  onClick={() => onEditWidget?.(block.widgetId)}
                                >
                                  Edit
                                </button>
                              ) : null}
                              {block.widgetId ? (
                                <button
                                  className="px-2 py-1 bg-gray-800 rounded text-xs"
                                  onClick={() => updateBlock(idx, { widgetId: null })}
                                >
                                  Clear
                                </button>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                  </>
                ))}

                {renderMainPanel('translations', 'Translations', (
                <div className="bg-gray-900 rounded p-4">
                  <div className="text-lg font-semibold mb-2">Translations</div>
                  <div className="text-sm text-gray-400 mb-3">
                    Compare any two languages, keep one source language, and edit the working pane directly.
                  </div>
                  <div className="text-xs text-gray-500 mb-4">
                    Last translation model: {currentTranslatorLabel}
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
                    <label className="text-sm text-gray-200">
                      Source language
                      <select
                        className="mt-1 w-full px-3 py-2 rounded bg-gray-950 text-white border border-gray-800"
                        value={sourceLanguageCode}
                        onChange={(e) => handlePromoteSourceLanguage(e.target.value)}
                      >
                        {allLanguageCodes.map((code) => (
                          <option key={`workspace-source-${code}`} value={code}>
                            {getLanguageLabel(code)} ({code})
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-sm text-gray-200">
                      Compare from
                      <select
                        className="mt-1 w-full px-3 py-2 rounded bg-gray-950 text-white border border-gray-800"
                        value={compareFromCode}
                        onChange={(e) => setCompareFromCode(e.target.value)}
                      >
                        {allLanguageCodes.map((code) => (
                          <option key={`compare-from-${code}`} value={code}>
                            {getLanguageLabel(code)} ({code})
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-sm text-gray-200">
                      Compare to
                      <select
                        className="mt-1 w-full px-3 py-2 rounded bg-gray-950 text-white border border-gray-800"
                        value={compareToCode}
                        onChange={(e) => setCompareToCode(e.target.value)}
                      >
                        {allLanguageCodes.map((code) => (
                          <option key={`compare-to-${code}`} value={code}>
                            {getLanguageLabel(code)} ({code})
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <button
                      className="px-3 py-1 rounded bg-indigo-600 text-white"
                      onClick={handleTranslate}
                      disabled={translating || saving || humanizing}
                    >
                      {translating ? 'Translating…' : 'Translate Right from Source'}
                    </button>
                    <button
                      className="px-3 py-1 rounded bg-gray-700 text-white disabled:bg-gray-800 disabled:text-gray-500"
                      onClick={handleCopyLeftToRight}
                      disabled={!compareFromCode || !compareToCode || sameLanguageCode(compareFromCode, compareToCode)}
                    >
                      Copy Left to Right
                    </button>
                  </div>

                  <label className="mb-4 flex items-start gap-3 rounded border border-gray-800 bg-gray-950/70 px-3 py-3 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      className="mt-0.5 accent-indigo-500"
                      checked={maintainTranslationTitles}
                      onChange={(e) => setMaintainTranslationTitles(e.target.checked)}
                    />
                    <span>
                      <span className="block font-semibold text-white">Maintain titles when translating</span>
                      <span className="mt-1 block text-xs text-gray-400">
                        Enabled by default so plaque titles stay in the original language while the body text is translated.
                      </span>
                    </span>
                  </label>

                  {translationMessage && (
                    <div className="mb-4 text-sm text-cyan-200">{translationMessage}</div>
                  )}

                  <div className="rounded border border-gray-800 bg-gray-950/70 p-3 mb-4">
                      <div className="text-xs uppercase tracking-wide text-gray-500 mb-3">Language Rail</div>
                      <div className="flex gap-3 overflow-x-auto pb-1">
                        {languageVersions.length === 0 && (
                          <div className="text-sm text-gray-500">Only the source language exists so far.</div>
                        )}
                        {languageVersions.map((row) => {
                          const badge = getFreshnessBadge(row);
                          const originBadge = getOriginBadge(row);
                          const isActive = sameLanguageCode(row.languageCode, compareFromCode) || sameLanguageCode(row.languageCode, compareToCode);
                          return (
                            <div
                              key={`language-row-${row.languageCode}`}
                              className={`min-w-[240px] rounded border p-3 ${isActive ? 'border-indigo-500 bg-indigo-950/30' : 'border-gray-800 bg-gray-950/40'}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-white truncate">
                                    {getLanguageLabel(row.languageCode)}
                                  </div>
                                  <div className="text-xs text-gray-500">{row.languageCode}</div>
                                </div>
                                <div className="flex shrink-0 flex-col items-end gap-1">
                                  <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${badge.classes}`}>
                                    {badge.label}
                                  </span>
                                  {originBadge && (
                                    <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${originBadge.classes}`}>
                                      {originBadge.label}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="mt-2 flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  className="rounded bg-gray-800 px-2 py-1 text-[11px] text-white"
                                  onClick={() => setCompareFromCode(row.languageCode)}
                                >
                                  Set Left
                                </button>
                                <button
                                  type="button"
                                  className="rounded bg-gray-800 px-2 py-1 text-[11px] text-white"
                                  onClick={() => setCompareToCode(row.languageCode)}
                                >
                                  Set Right
                                </button>
                                {!row.isSource && (
                                  <button
                                    type="button"
                                    className="rounded bg-indigo-700 px-2 py-1 text-[11px] text-white"
                                    onClick={() => handlePromoteSourceLanguage(row.languageCode)}
                                  >
                                    Make Source
                                  </button>
                                )}
                                {!row.isSource && (
                                  <button
                                    type="button"
                                    className="rounded bg-red-700 px-2 py-1 text-[11px] text-white disabled:bg-red-950 disabled:text-red-200/60"
                                    onClick={() => handleRemoveTranslation(row.languageCode)}
                                    disabled={removingTranslationCode === row.languageCode || translating || saving || humanizing}
                                  >
                                    {removingTranslationCode === row.languageCode ? 'Removing…' : 'Remove'}
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 2xl:grid-cols-2 gap-4">
                      <div className="rounded border border-gray-800 bg-gray-950/70 p-4">
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <div>
                            <div className="text-sm font-semibold text-white">
                              Left: {getLanguageLabel(compareFromCode)}
                            </div>
                            <div className="text-xs text-gray-500">{compareFromCode}</div>
                          </div>
                          {compareFromVersion ? (
                            <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${getFreshnessBadge(compareFromVersion).classes}`}>
                              {getFreshnessBadge(compareFromVersion).label}
                            </span>
                          ) : (
                            <span className="rounded-full bg-gray-800 px-2 py-1 text-[11px] font-semibold text-gray-200">
                              Missing
                            </span>
                          )}
                        </div>
                        <label className="block text-xs text-gray-300 mb-1">Title</label>
                        <input
                          className="w-full px-3 py-2 rounded bg-gray-900 text-gray-200 border border-gray-800 mb-3"
                          value={compareFromVersion?.title || ''}
                          readOnly
                        />
                        <label className="block text-xs text-gray-300 mb-1">Body</label>
                        <textarea
                          className="w-full min-h-64 px-3 py-2 rounded bg-gray-900 text-gray-200 border border-gray-800 mb-3"
                          value={compareFromVersion?.bodyText || compareFromVersion?.translatedText || ''}
                          readOnly
                        />
                        <label className="block text-xs text-gray-300 mb-1">Map Label</label>
                        <input
                          className="w-full px-3 py-2 rounded bg-gray-900 text-gray-200 border border-gray-800"
                          value={compareFromVersion?.mapYouAreHereLabel || ''}
                          readOnly
                        />
                      </div>

                      <div className="rounded border border-gray-800 bg-gray-950/70 p-4">
                        <div className="flex items-center justify-between gap-2 mb-3">
                          <div>
                            <div className="text-sm font-semibold text-white">
                              Right: {getLanguageLabel(compareToCode)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {compareToCode}
                              {sameLanguageCode(compareToCode, sourceLanguageCode) ? ' · editing the source language' : ''}
                            </div>
                          </div>
                          {compareToVersion ? (
                            <div className="flex flex-col items-end gap-1">
                              <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${getFreshnessBadge(compareToVersion).classes}`}>
                                {getFreshnessBadge(compareToVersion).label}
                              </span>
                              {getOriginBadge(compareToVersion) && (
                                <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${getOriginBadge(compareToVersion).classes}`}>
                                  {getOriginBadge(compareToVersion).label}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="rounded-full bg-gray-800 px-2 py-1 text-[11px] font-semibold text-gray-200">
                              Missing
                            </span>
                          )}
                        </div>
                        <label className="block text-xs text-gray-300 mb-1">Title</label>
                        <input
                          className="w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800 mb-3"
                          value={compareToVersion?.title || ''}
                          onChange={(e) => updateLanguageVersion(compareToCode, { title: e.target.value, autoGenerated: false, reviewStatus: 'draft' })}
                        />
                        <label className="block text-xs text-gray-300 mb-1">Body</label>
                        <textarea
                          className="w-full min-h-64 px-3 py-2 rounded bg-gray-900 text-white border border-gray-800 mb-3"
                          value={compareToVersion?.bodyText || compareToVersion?.translatedText || ''}
                          onChange={(e) => updateLanguageVersion(compareToCode, { bodyText: e.target.value, autoGenerated: false, reviewStatus: 'draft' })}
                          placeholder={`Add or edit ${getLanguageLabel(compareToCode)} text…`}
                        />
                        <label className="block text-xs text-gray-300 mb-1">Map Label</label>
                        <input
                          className="w-full px-3 py-2 rounded bg-gray-900 text-white border border-gray-800 mb-3"
                          value={compareToVersion?.mapYouAreHereLabel || ''}
                          onChange={(e) => updateLanguageVersion(compareToCode, { mapYouAreHereLabel: e.target.value, autoGenerated: false, reviewStatus: 'draft' })}
                          placeholder="Optional map marker label"
                        />
                        {!sameLanguageCode(compareToCode, sourceLanguageCode) && (
                          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-300">
                            <span className="text-gray-400">Review</span>
                            <button
                              type="button"
                              className={`rounded px-2 py-1 ${normalizeReviewStatus(compareToVersion?.reviewStatus, 'draft') === 'draft' ? 'bg-gray-700 text-white' : 'bg-gray-900 text-gray-300 border border-gray-800'}`}
                              onClick={() => handleSetReviewStatus(compareToCode, 'draft')}
                            >
                              Draft
                            </button>
                            <button
                              type="button"
                              className={`rounded px-2 py-1 ${normalizeReviewStatus(compareToVersion?.reviewStatus, 'draft') === 'reviewed' ? 'bg-lime-800 text-lime-100' : 'bg-gray-900 text-gray-300 border border-gray-800'}`}
                              onClick={() => handleSetReviewStatus(compareToCode, 'reviewed')}
                            >
                              Reviewed
                            </button>
                            <button
                              type="button"
                              className={`rounded px-2 py-1 ${normalizeReviewStatus(compareToVersion?.reviewStatus, 'draft') === 'approved' ? 'bg-emerald-800 text-emerald-100' : 'bg-gray-900 text-gray-300 border border-gray-800'}`}
                              onClick={() => handleSetReviewStatus(compareToCode, 'approved')}
                            >
                              Approved
                            </button>
                          </div>
                        )}
                      </div>
                  </div>
                </div>
                ))}

                {!isActivityHubSurface && renderMainPanel('autoPromotions', 'Auto Promotions', (
                  <div className="bg-gray-900 rounded p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-xs text-gray-400">
                          Read-only preview of the saved plaque&apos;s automatic monetisation output for the current language view.
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          className="rounded bg-gray-800 px-3 py-2 text-xs text-white hover:bg-gray-700 disabled:opacity-50"
                          onClick={refreshPromotionPreview}
                          disabled={!activePlaqueId || !landingKey || promotionPreviewLoading}
                        >
                          {promotionPreviewLoading ? 'Refreshing…' : 'Refresh Preview'}
                        </button>
                        <button
                          type="button"
                          className="rounded bg-indigo-600 px-3 py-2 text-xs text-white hover:bg-indigo-500 disabled:opacity-50"
                          onClick={openLandingPage}
                          disabled={!previewLandingUrl}
                        >
                          Open Public Page
                        </button>
                      </div>
                    </div>

                    {!activePlaqueId && (
                      <div className="mt-3 rounded border border-gray-800 bg-gray-950/70 px-3 py-3 text-sm text-gray-400">
                        Save the plaque first to inspect automatic promotions.
                      </div>
                    )}

                    {activePlaqueId && !landingKey && (
                      <div className="mt-3 rounded border border-amber-800 bg-amber-950/40 px-3 py-3 text-sm text-amber-200">
                        This plaque does not have a landing key yet. Save or publish it before loading the promotion preview.
                      </div>
                    )}

                    {activePlaqueId && landingKey && (
                      <div className="mt-4 space-y-4">
                        <div className="grid grid-cols-1 gap-3 lg:grid-cols-4">
                          <div className="rounded border border-gray-800 bg-gray-950/70 px-3 py-3">
                            <div className="text-[11px] uppercase tracking-wide text-gray-500">Editor Selection</div>
                            <div className="mt-1 text-sm font-semibold text-white">
                              {getMonetisationModeLabel(effectiveMonetisationMode)}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              {getLanguageLabel(previewLanguageCode)} preview
                            </div>
                          </div>
                          <div className="rounded border border-gray-800 bg-gray-950/70 px-3 py-3">
                            <div className="text-[11px] uppercase tracking-wide text-gray-500">Saved Effective Mode</div>
                            <div className="mt-1 text-sm font-semibold text-white">
                              {promotionPreview?.monetisation
                                ? getMonetisationModeLabel(promotionPreview.monetisation.effectiveMode)
                                : '—'}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              {promotionPreview?.monetisation?.isEnforced ? 'Enforced by subscription' : 'Resolved from saved settings'}
                            </div>
                          </div>
                          <div className="rounded border border-gray-800 bg-gray-950/70 px-3 py-3">
                            <div className="text-[11px] uppercase tracking-wide text-gray-500">Linked Destination</div>
                            <div className="mt-1 text-sm font-semibold text-white">
                              {promotionPreview?.activityContext?.destination?.destinationSlug || 'None linked'}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              {promotionPreview?.activityContext?.source || 'No destination source'}
                            </div>
                          </div>
                          <div className="rounded border border-gray-800 bg-gray-950/70 px-3 py-3">
                            <div className="text-[11px] uppercase tracking-wide text-gray-500">Activity Candidates</div>
                            <div className="mt-1 text-sm font-semibold text-white">
                              {Array.isArray(promotionPreview?.activityContext?.candidates)
                                ? promotionPreview.activityContext.candidates.length
                                : 0}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              {promotionPreview?.activityContext?.skipAutoInsert ? 'Auto insert skipped' : 'Available for inline links and cards'}
                            </div>
                          </div>
                        </div>

                        {promotionPreviewError && (
                          <div className="rounded border border-red-800 bg-red-950/40 px-3 py-3 text-sm text-red-200">
                            {promotionPreviewError}
                          </div>
                        )}

                        {!promotionPreviewError && promotionPreviewLoading && !promotionPreview && (
                          <div className="rounded border border-gray-800 bg-gray-950/70 px-3 py-3 text-sm text-gray-400">
                            Loading monetisation preview…
                          </div>
                        )}

                        {!promotionPreviewError && promotionPreview && (
                          <>
                            {monetisationMode !== savedMonetisationMode && (
                              <div className="rounded border border-amber-800 bg-amber-950/40 px-3 py-3 text-sm text-amber-200">
                                The preview is based on the last saved plaque state. Save changes to update the generated promotion output.
                              </div>
                            )}

                            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                              <div className="rounded border border-gray-800 bg-gray-950/70 p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <div>
                                    <div className="text-sm font-semibold text-white">Inline Links</div>
                                    <div className="text-xs text-gray-500">
                                      Auto-inserted subtle links chosen for this plaque.
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {Array.isArray(promotionPreview.insertedInlineLinks) ? promotionPreview.insertedInlineLinks.length : 0} selected
                                  </div>
                                </div>
                                <div className="mt-3 space-y-2">
                                  {(Array.isArray(promotionPreview.insertedInlineLinks) ? promotionPreview.insertedInlineLinks : []).length > 0 ? (
                                    (promotionPreview.insertedInlineLinks || []).map((link, index) => (
                                      <div key={`${link.url || 'link'}-${index}`} className="rounded border border-gray-800 bg-gray-900/80 px-3 py-2">
                                        <div className="text-sm font-medium text-white">{link.title || 'Untitled link'}</div>
                                        <div className="mt-1 break-all text-xs text-gray-400">{link.url || '—'}</div>
                                        <div className="mt-1 text-[11px] uppercase tracking-wide text-gray-500">{link.source || 'unknown'}</div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="rounded border border-dashed border-gray-800 px-3 py-3 text-sm text-gray-500">
                                      No inline links would currently be inserted.
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="rounded border border-gray-800 bg-gray-950/70 p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <div>
                                    <div className="text-sm font-semibold text-white">Activity Cards</div>
                                    <div className="text-xs text-gray-500">
                                      Cards that appear for `standard` and `full` modes.
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {Array.isArray(promotionPreview?.activityBlockSection?.cards) ? promotionPreview.activityBlockSection.cards.length : 0} selected
                                  </div>
                                </div>
                                <div className="mt-3 space-y-2">
                                  {(Array.isArray(promotionPreview?.activityBlockSection?.cards) ? promotionPreview.activityBlockSection.cards : []).length > 0 ? (
                                    (promotionPreview.activityBlockSection.cards || []).map((card, index) => (
                                      <div key={`${card.targetUrl || 'card'}-${index}`} className="rounded border border-gray-800 bg-gray-900/80 px-3 py-2">
                                        <div className="text-sm font-medium text-white">{card.title || 'Untitled card'}</div>
                                        {card.body && (
                                          <div className="mt-1 text-xs text-gray-300">{card.body}</div>
                                        )}
                                        <div className="mt-1 break-all text-xs text-gray-400">{card.targetUrl || '—'}</div>
                                        <div className="mt-1 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-gray-500">
                                          <span>{card.ctaText || 'No CTA'}</span>
                                          <span>{card.source || 'unknown'}</span>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="rounded border border-dashed border-gray-800 px-3 py-3 text-sm text-gray-500">
                                      No activity cards would currently be inserted.
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="rounded border border-gray-800 bg-gray-950/70 p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <div>
                                    <div className="text-sm font-semibold text-white">Bottom Widgets</div>
                                    <div className="text-xs text-gray-500">
                                      Widgets that appear only in `full` mode.
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {Array.isArray(promotionPreview?.fullModeWidgetSection?.widgets) ? promotionPreview.fullModeWidgetSection.widgets.length : 0} selected
                                  </div>
                                </div>
                                <div className="mt-3 space-y-2">
                                  {(Array.isArray(promotionPreview?.fullModeWidgetSection?.widgets) ? promotionPreview.fullModeWidgetSection.widgets : []).length > 0 ? (
                                    (promotionPreview.fullModeWidgetSection.widgets || []).map((widget) => (
                                      <div key={widget.id} className="rounded border border-gray-800 bg-gray-900/80 px-3 py-2">
                                        <div className="text-sm font-medium text-white">{widget.title || 'Untitled widget'}</div>
                                        <div className="mt-1 flex flex-wrap gap-2 text-[11px] uppercase tracking-wide text-gray-500">
                                          <span>{widget.partner || 'Unknown partner'}</span>
                                          <span>{widget.widgetType || 'Unknown type'}</span>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="rounded border border-dashed border-gray-800 px-3 py-3 text-sm text-gray-500">
                                      No bottom widgets would currently be inserted.
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="rounded border border-gray-800 bg-gray-950/70 p-4">
                                <div className="flex items-center justify-between gap-3">
                                  <div>
                                    <div className="text-sm font-semibold text-white">Resolved Activity Targets</div>
                                    <div className="text-xs text-gray-500">
                                      Destination hub pages currently available to the monetisation resolver.
                                    </div>
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    {Array.isArray(promotionPreview?.activityContext?.candidates) ? promotionPreview.activityContext.candidates.length : 0} available
                                  </div>
                                </div>
                                <div className="mt-3 space-y-2">
                                  {(Array.isArray(promotionPreview?.activityContext?.candidates) ? promotionPreview.activityContext.candidates : []).length > 0 ? (
                                    (promotionPreview.activityContext.candidates || []).map((candidate) => (
                                      <div key={candidate.hubId || candidate.url} className="rounded border border-gray-800 bg-gray-900/80 px-3 py-2">
                                        <div className="text-sm font-medium text-white">{candidate.title || candidate.areaName || 'Untitled activity'}</div>
                                        <div className="mt-1 flex flex-wrap gap-2 text-xs text-gray-400">
                                          <span>{candidate.areaName || candidate.areaSlug || 'Unknown area'}</span>
                                          <span>{candidate.activitiesCount || 0} activities</span>
                                        </div>
                                        <div className="mt-1 break-all text-[11px] text-gray-500">{candidate.url || '—'}</div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="rounded border border-dashed border-gray-800 px-3 py-3 text-sm text-gray-500">
                                      No activity targets are currently linked to this plaque.
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                </div>
              </div>

              <div className={`min-h-0 min-w-0 ${sidebarCollapsed ? 'lg:w-[4.75rem] lg:flex-none' : 'lg:w-[24rem] lg:flex-none'}`}>
                <div className="h-full min-h-0 overflow-y-auto overscroll-contain">
                <div className="space-y-4 min-w-0">
                <div className={`sticky top-0 z-10 rounded bg-gray-900/95 p-2 backdrop-blur ${sidebarCollapsed ? 'flex flex-col items-center gap-2' : 'flex items-center justify-between gap-2'}`}>
                  <div className={`flex gap-2 ${sidebarCollapsed ? 'flex-col items-center' : 'flex-wrap'}`}>
                    {sidebarTabs.map((tab) => {
                      const Icon = tab.icon;
                      const active = rightTab === tab.key;
                      return (
                        <button
                          key={tab.key}
                          type="button"
                          className={`flex items-center justify-center gap-2 rounded ${sidebarCollapsed ? 'h-11 w-11' : 'px-3 py-2'} text-sm ${
                            active ? 'bg-indigo-600 text-white' : 'bg-gray-800 text-gray-300'
                          }`}
                          onClick={() => {
                            setRightTab(tab.key);
                            if (sidebarCollapsed) setSidebarCollapsed(false);
                          }}
                          title={tab.label}
                        >
                          <Icon size={18} />
                          {!sidebarCollapsed && <span>{tab.label}</span>}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className={`flex items-center justify-center rounded bg-gray-800 text-gray-200 ${sidebarCollapsed ? 'h-11 w-11' : 'px-3 py-2'}`}
                    onClick={() => setSidebarCollapsed((prev) => !prev)}
                    title={sidebarCollapsed ? 'Expand tools' : 'Collapse tools'}
                  >
                    {sidebarCollapsed ? <ChevronLeft size={18} /> : <ChevronRight size={18} />}
                  </button>
                </div>

                {!sidebarCollapsed && rightTab === 'preview' && (
                  <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-semibold">Landing Preview</h3>
                        <div className="text-xs text-gray-400">
                          Saved landing page in {getLanguageLabel(previewLanguageCode)} ({previewLanguageCode})
                        </div>
                      </div>
                      <button
                        type="button"
                        className="rounded bg-indigo-600 px-3 py-2 text-sm text-white disabled:bg-indigo-900 disabled:text-gray-400"
                        onClick={openLandingPage}
                        disabled={!previewLandingUrl}
                      >
                        Open Landing Page
                      </button>
                    </div>
                    {!previewLandingUrl ? (
                      <div className="text-sm text-gray-400">
                        Save the plaque first to preview its landing page.
                      </div>
                    ) : (
                      <div className="overflow-hidden rounded border border-gray-800 bg-white">
                        <iframe
                          key={previewLandingUrl}
                          title={`Landing preview ${previewLanguageCode}`}
                          src={previewLandingUrl}
                          className="h-[70vh] w-full bg-white"
                        />
                      </div>
                    )}
                  </div>
                )}

                {!sidebarCollapsed && rightTab === 'map' && (
                  <>
                    <div className="bg-gray-900 rounded p-4">
                      <div className="text-lg font-semibold mb-2">Map Marker</div>
                      {availableMaps.length === 0 && (
                        <div className="text-sm text-gray-400">
                          No site maps available yet.
                          <button
                            type="button"
                            className="ml-2 text-indigo-300 underline"
                            onClick={() => {
                              const params = new URLSearchParams(window.location.search || '');
                              params.set('settingsSubTab', 'Site Maps');
                              window.location.href = `${window.location.pathname}?${params.toString()}`;
                            }}
                          >
                          Open Site Maps
                          </button>
                        </div>
                      )}
                      {availableMaps.length > 0 && (
                        <div className="space-y-3">
                          <label className="block text-sm text-gray-200">
                            Map
                            <select
                              className="mt-1 w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700"
                              value={selectedMapId || ''}
                              onChange={(e) => setSelectedMapId(e.target.value || null)}
                            >
                              <option value="">No map</option>
                              {availableMaps.map((map) => (
                                <option key={map.id} value={map.id}>
                                  {map.name || `Map #${map.id}`}
                                </option>
                              ))}
                            </select>
                          </label>

                          {selectedMap?.imageUrl && (
                            <div className="rounded border border-gray-800 overflow-hidden relative">
                              <img src={selectedMap.imageUrl} alt="Site map" className="w-full h-auto" />
                              {mapPlaques
                                .filter((plaque) => plaque.map_marker_enabled && plaque.map_marker_x !== null && plaque.map_marker_y !== null)
                                .map((plaque) => {
                                  const left = `${(plaque.map_marker_x || 0) * 100}%`;
                                  const top = `${(plaque.map_marker_y || 0) * 100}%`;
                                  const legendNumber = legendNumberById.get(plaque.id);
                                  return (
                                    <div
                                      key={`plaque-map-marker-${plaque.id}`}
                                      className="absolute w-6 h-6 rounded-full bg-black/70 text-white text-xs font-semibold flex items-center justify-center border border-white/70"
                                      style={{
                                        left,
                                        top,
                                        transform: 'translate(-50%, -50%)',
                                        width: `calc(28px * ${mapLabelScale})`,
                                        height: `calc(28px * ${mapLabelScale})`,
                                        fontSize: `calc(0.75rem * ${mapLabelScale})`,
                                        opacity: mapLabelOpacity,
                                      }}
                                      title={plaque.title}
                                    >
                                      {legendNumber || '•'}
                                    </div>
                                  );
                                })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="bg-gray-900 rounded p-4">
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="text-lg font-semibold">QR Code</div>
                        <button
                          className="px-3 py-1 rounded bg-gray-700 text-white text-sm"
                          onClick={copyQrToClipboard}
                          disabled={!qrDataUrl}
                        >
                          Copy to Clipboard
                        </button>
                      </div>
                      {!landingUrl && (
                        <div className="text-sm text-gray-400">Save the plaque to generate its QR code.</div>
                      )}
                      {landingUrl && qrDataUrl && (
                        <div className="bg-white rounded p-3 flex items-center justify-center">
                          <img src={qrDataUrl} alt="Plaque QR code" style={{ width: 320, height: 320 }} />
                        </div>
                      )}
                      {landingUrl && !qrDataUrl && (
                        <div className="text-sm text-gray-400">Generating QR code…</div>
                      )}
                    </div>
                  </>
                )}

                {!sidebarCollapsed && rightTab === 'campaignAds' && (
                  <div className="space-y-4">
                    <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                      <h3 className="text-lg font-semibold mb-3">Campaign Ads</h3>
                      <label className="mb-4 flex items-center gap-2 text-sm text-gray-200">
                        <input
                          type="checkbox"
                          checked={campaignAdsDynamicMode}
                          onChange={(e) => setCampaignAdsDynamicMode(e.target.checked)}
                        />
                        Dynamic Ad
                      </label>
                      <div className="flex flex-wrap gap-3 mb-4">
                        <input
                          className="flex-1 min-w-[200px] rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                          placeholder="Search campaigns"
                          value={campaignAdsFilter.q}
                          onChange={(e) => setCampaignAdsFilter({ q: e.target.value })}
                        />
                        <button
                          className="px-3 py-2 rounded bg-gray-800 text-white text-sm"
                          onClick={loadCampaignAds}
                          disabled={campaignAdsLoading}
                        >
                          {campaignAdsLoading ? 'Loading…' : 'Refresh'}
                        </button>
                      </div>
                      {campaignAdsError && (
                        <div className="text-sm text-red-300 mb-3">{campaignAdsError}</div>
                      )}
                      {campaignAdsLoading && <div className="text-gray-400">Loading campaign ads...</div>}
                      {!campaignAdsLoading && filteredCampaignAds.length === 0 && (
                        <div className="text-gray-400">
                          {campaignAds.length === 0
                            ? 'No campaigns found in this account yet.'
                            : 'No campaign ads match this search.'}
                        </div>
                      )}
                      <div className="space-y-3 max-h-[45vh] overflow-auto pr-1">
                        {filteredCampaignAds.map((campaignAd) => {
                          const isSelected = campaignAdsDynamicMode
                            ? selectedCampaignAdIds.some((campaignId) => String(campaignId) === String(campaignAd.campaignId))
                            : String(campaignAd.campaignId) === String(selectedCampaignAdId || '');
                          return (
                            <div
                              key={campaignAd.campaignId}
                              role="button"
                              tabIndex={0}
                              className={`w-full text-left rounded border px-3 py-3 ${
                                isSelected
                                  ? 'border-indigo-500 bg-indigo-950/40'
                                  : 'border-gray-800 bg-gray-950/60 hover:bg-gray-800/60'
                              }`}
                              onClick={() => toggleCampaignAdSelection(campaignAd.campaignId)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleCampaignAdSelection(campaignAd.campaignId);
                                }
                              }}
                            >
                              <div className="flex items-start gap-3">
                                <div className="h-20 w-20 flex-none overflow-hidden rounded border border-gray-800 bg-gray-900">
                                  {campaignAd.previewImageUrl ? (
                                    <img
                                      src={campaignAd.previewImageUrl}
                                      alt={campaignAd.title || 'Campaign preview'}
                                      className="h-full w-full object-cover"
                                      loading="lazy"
                                    />
                                  ) : (
                                    <div className="flex h-full items-center justify-center px-2 text-center text-xs text-gray-500">
                                      No artwork
                                    </div>
                                  )}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div className="min-w-0">
                                      <div className="text-sm text-gray-400">Campaign #{campaignAd.campaignId}</div>
                                      <div className="truncate text-base font-semibold">
                                        {campaignAd.title || `Campaign #${campaignAd.campaignId}`}
                                      </div>
                                      <div className="truncate text-xs text-gray-400">
                                        {campaignAd.siteName || 'Unknown site'}
                                      </div>
                                    </div>
                                    <label className="flex items-center gap-2 text-xs text-indigo-300" onClick={(e) => e.stopPropagation()}>
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        readOnly
                                      />
                                      {isSelected ? 'Selected' : 'Select'}
                                    </label>
                                  </div>
                                  <div className="mt-2 text-xs text-gray-400">
                                    Target: {campaignAd.landingKey ? `/lp/c/${campaignAd.landingKey}` : 'No landing page'}
                                  </div>
                                  {campaignAd.landingUrl ? (
                                    <div className="mt-2">
                                      <a
                                        href={campaignAd.landingUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-xs text-indigo-300 underline"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        Open Landing Page
                                      </a>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                      <div className="mb-3">
                        <h3 className="text-lg font-semibold">Selection Preview</h3>
                        <div className="text-xs text-gray-400">
                          {campaignAdsDynamicMode
                            ? 'Select multiple campaigns and insert them as one dynamic block.'
                            : 'Select a campaign and insert it into the plaque as a reorderable content block.'}
                        </div>
                      </div>
                      {campaignAdsDynamicMode ? (
                        !selectedCampaignAds.length ? (
                          <div className="text-sm text-gray-400">No campaigns selected.</div>
                        ) : (
                          <div className="space-y-3">
                            <div className="text-sm text-gray-300">
                              {selectedCampaignAds.length} campaign{selectedCampaignAds.length === 1 ? '' : 's'} selected for round-robin rotation.
                            </div>
                            <div className="space-y-2">
                              {selectedCampaignAds.map((campaignAd, dynamicIndex) => (
                                <div
                                  key={`selected-dynamic-campaign-${campaignAd.campaignId}`}
                                  className={`rounded border p-3 ${getBlockFieldClasses('campaign_dynamic')}`}
                                >
                                  <div className="flex items-start gap-3">
                                    <div className="h-14 w-14 flex-none overflow-hidden rounded border border-gray-800 bg-gray-950">
                                      {campaignAd.previewImageUrl ? (
                                        <img
                                          src={campaignAd.previewImageUrl}
                                          alt={campaignAd.title || `Campaign #${campaignAd.campaignId}`}
                                          className="h-full w-full object-cover"
                                          loading="lazy"
                                        />
                                      ) : (
                                        <div className="flex h-full items-center justify-center text-[10px] text-gray-500">
                                          No art
                                        </div>
                                      )}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="font-semibold">
                                        {campaignAd.title || `Campaign #${campaignAd.campaignId}`}
                                      </div>
                                      <div className="text-xs text-gray-400">
                                        {campaignAd.siteName || 'Unknown site'}
                                      </div>
                                      <div className="text-xs text-gray-400">
                                        Rotation order {dynamicIndex + 1}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <button
                              type="button"
                              className="inline-flex rounded bg-indigo-600 px-3 py-2 text-sm text-white disabled:bg-indigo-900 disabled:text-gray-400"
                              onClick={addDynamicCampaignBlock}
                              disabled={!selectedCampaignAds.length}
                            >
                              Add Dynamic Ad
                            </button>
                          </div>
                        )
                      ) : (
                        !selectedCampaignAd ? (
                          <div className="text-sm text-gray-400">No campaign selected.</div>
                        ) : (
                          <div className="space-y-3">
                            <div className="overflow-hidden rounded border border-gray-800 bg-gray-950">
                              {selectedCampaignAd.previewImageUrl ? (
                                <img
                                  src={selectedCampaignAd.previewImageUrl}
                                  alt={selectedCampaignAd.title || 'Selected campaign'}
                                  className="h-48 w-full object-cover"
                                  loading="lazy"
                                />
                              ) : (
                                <div className="flex h-48 items-center justify-center text-sm text-gray-500">
                                  No artwork available.
                                </div>
                              )}
                            </div>
                            <div>
                              <div className="text-lg font-semibold">
                                {selectedCampaignAd.title || `Campaign #${selectedCampaignAd.campaignId}`}
                              </div>
                              <div className="text-sm text-gray-400">
                                {selectedCampaignAd.siteName || 'Unknown site'}
                              </div>
                            </div>
                            <div className="text-xs text-gray-400">
                              Landing key: {selectedCampaignAd.landingKey || 'Not available'}
                            </div>
                            {selectedCampaignAd.landingUrl ? (
                              <a
                                href={selectedCampaignAd.landingUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex rounded bg-gray-800 px-3 py-2 text-sm text-white hover:bg-gray-700"
                              >
                                Open Landing Page
                              </a>
                            ) : null}
                            <button
                              type="button"
                              className="inline-flex rounded bg-indigo-600 px-3 py-2 text-sm text-white disabled:bg-indigo-900 disabled:text-gray-400"
                              onClick={addCampaignBlock}
                              disabled={!selectedCampaignAd}
                            >
                              Add Selected Ad
                            </button>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                {!sidebarCollapsed && rightTab === 'assets' && (
                  <div className="space-y-4">
                    <PhotoAssetBrowser
                      ref={photoBrowserRef}
                      assets={assets}
                      siteId={siteId}
                      useFolders
                      promptText={(title || '').trim() ? `A high-quality photo for: ${title}` : ''}
                      campaignTitle={title}
                      multiSelect
                      selectedAssetId={selectedLibraryAsset?.id || null}
                      selectedAssetIds={selectedLibraryAssetIds}
                      onToggleAsset={toggleLibraryAssetSelection}
                      onUploadComplete={() => {
                        onRefreshAssets?.();
                      }}
                      leadingAction={
                        <>
                          <button
                            type="button"
                            className="px-3 py-2 rounded text-sm text-white bg-emerald-600 hover:bg-emerald-500 disabled:bg-emerald-900"
                            onClick={addImageBlockFromSelection}
                            disabled={!selectedLibraryAssetIds.length}
                          >
                            {selectedLibraryAssetIds.length > 1 ? `Add ${selectedLibraryAssetIds.length} Selected Images` : 'Add Selected Image'}
                          </button>
                          <button
                            type="button"
                            className="px-3 py-2 rounded text-sm text-white bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:text-gray-400"
                            onClick={() => setSelectedLibraryAssetIds([])}
                            disabled={!selectedLibraryAssetIds.length}
                          >
                            Clear Selection
                          </button>
                        </>
                      }
                    />
                    <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                      <div className="mb-3 flex items-center justify-between gap-3">
                        <div>
                          <h3 className="text-lg font-semibold">Selection Preview</h3>
                          <div className="text-xs text-gray-400">
                            {selectedLibraryAssetIds.length
                              ? `${selectedLibraryAssetIds.length} image${selectedLibraryAssetIds.length === 1 ? '' : 's'} selected.`
                              : 'Click images to select or unselect them.'}
                          </div>
                        </div>
                      </div>
                      <SelectedAssetPreview asset={selectedLibraryAsset} />
                    </div>
                  </div>
                )}

                {!sidebarCollapsed && isAdmin && rightTab === 'ads' && (
                  <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                    <h3 className="text-lg font-semibold mb-3">Ad Library</h3>
                    <div className="flex flex-wrap gap-3 mb-4">
                      <input
                        className="flex-1 min-w-[200px] rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                        placeholder="Search ads"
                        value={adFilter.q}
                        onChange={(e) => setAdFilter((prev) => ({ ...prev, q: e.target.value }))}
                      />
                      <select
                        className="rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                        value={adFilter.status}
                        onChange={(e) => setAdFilter((prev) => ({ ...prev, status: e.target.value }))}
                      >
                        <option value="all">All statuses</option>
                        <option value="draft">draft</option>
                        <option value="active">active</option>
                        <option value="archived">archived</option>
                      </select>
                      <button
                        className="px-3 py-2 rounded bg-gray-800 text-white text-sm"
                        onClick={loadAds}
                        disabled={adsLoading}
                      >
                        {adsLoading ? 'Loading…' : 'Refresh'}
                      </button>
                    </div>
                    {allAdTags.length > 0 && (
                      <div className="mb-4">
                        <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Filter Tags</div>
                        <TagFilter
                          value={adFilter.tags}
                          suggestions={allAdTags}
                          onChange={(tags) => setAdFilter((prev) => ({ ...prev, tags }))}
                        />
                      </div>
                    )}
                    {adsError && (
                      <div className="text-sm text-red-300 mb-3">{adsError}</div>
                    )}
                    {adsLoading && <div className="text-gray-400">Loading ads...</div>}
                    {!adsLoading && filteredAds.length === 0 && (
                      <div className="text-gray-400">No ads yet.</div>
                    )}
                    <div className="space-y-3 max-h-[45vh] overflow-auto pr-1">
                      {filteredAds.map((ad) => {
                        const isSelected = String(ad.id) === String(selectedAdId || '');
                        return (
                          <button
                            key={ad.id}
                            type="button"
                            className={`w-full text-left rounded border px-3 py-2 ${
                              isSelected
                                ? 'border-indigo-500 bg-indigo-950/40'
                                : 'border-gray-800 bg-gray-950/60 hover:bg-gray-800/60'
                            }`}
                            onClick={() => setSelectedAdId(ad.id)}
                          >
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <div>
                                <div className="text-sm text-gray-400">#{ad.id}</div>
                                <div className="text-base font-semibold">{ad.title}</div>
                                <div className="text-xs text-gray-400">{ad.status}</div>
                              </div>
                              {isSelected && (
                                <span className="text-xs text-indigo-300">Selected</span>
                              )}
                            </div>
                            <div className="mt-2 text-sm text-gray-300 line-clamp-2">
                              {ad.body}
                            </div>
                            {Array.isArray(ad.tags) && ad.tags.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {ad.tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className="px-2 py-1 rounded-full bg-gray-800 text-xs text-gray-200"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <button
                        className="px-3 py-2 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                        onClick={() => addAdBlock('inline')}
                        disabled={!selectedAd}
                      >
                        Add Inline Ad
                      </button>
                      {selectedAd?.image_id_standard ? (
                        <button
                          className="px-3 py-2 rounded bg-emerald-600 text-white"
                          onClick={() => addAdBlock('standard')}
                        >
                          Add Standard Banner
                        </button>
                      ) : null}
                      {selectedAd?.image_id_large ? (
                        <button
                          className="px-3 py-2 rounded bg-emerald-600 text-white"
                          onClick={() => addAdBlock('large')}
                        >
                          Add Large Banner
                        </button>
                      ) : null}
                      {selectedAd ? (
                        <div className="text-xs text-gray-400">
                          Selected: {selectedAd.title || `Ad #${selectedAd.id}`}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">Select an ad to add it inline.</div>
                      )}
                    </div>
                  </div>
                )}

                {!sidebarCollapsed && isAdmin && rightTab === 'widgets' && (
                  <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                    <h3 className="text-lg font-semibold mb-3">Widget Library</h3>
                    <div className="flex flex-wrap gap-3 mb-4">
                      <input
                        className="flex-1 min-w-[200px] rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                        placeholder="Search widgets"
                        value={widgetFilter.q}
                        onChange={(e) => setWidgetFilter((prev) => ({ ...prev, q: e.target.value }))}
                      />
                      <select
                        className="rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                        value={widgetFilter.status}
                        onChange={(e) => setWidgetFilter((prev) => ({ ...prev, status: e.target.value }))}
                      >
                        <option value="all">All statuses</option>
                        <option value="draft">draft</option>
                        <option value="active">active</option>
                        <option value="archived">archived</option>
                      </select>
                      <select
                        className="rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                        value={widgetFilter.partner}
                        onChange={(e) => setWidgetFilter((prev) => ({ ...prev, partner: e.target.value }))}
                      >
                        <option value="all">All partners</option>
                        <option value="GETYOURGUIDE">GetYourGuide</option>
                        <option value="TRAVELPAYOUTS">Travelpayouts</option>
                      </select>
                      <button
                        className="px-3 py-2 rounded bg-gray-800 text-white text-sm"
                        onClick={loadWidgets}
                        disabled={widgetsLoading}
                      >
                        {widgetsLoading ? 'Loading…' : 'Refresh'}
                      </button>
                    </div>
                    {allWidgetTags.length > 0 && (
                      <div className="mb-4">
                        <div className="text-xs uppercase tracking-widest text-gray-500 mb-2">Filter Tags</div>
                        <TagFilter
                          value={widgetFilter.tags}
                          suggestions={allWidgetTags}
                          onChange={(tags) => setWidgetFilter((prev) => ({ ...prev, tags }))}
                        />
                      </div>
                    )}
                    {widgetsError && (
                      <div className="text-sm text-red-300 mb-3">{widgetsError}</div>
                    )}
                    {widgetsLoading && <div className="text-gray-400">Loading widgets...</div>}
                    {!widgetsLoading && filteredWidgets.length === 0 && (
                      <div className="text-gray-400">No widgets yet.</div>
                    )}
                    <div className="space-y-3 max-h-[45vh] overflow-auto pr-1">
                      {filteredWidgets.map((widget) => {
                        const isSelected = String(widget.id) === String(selectedWidgetId || '');
                        return (
                          <div
                            key={widget.id}
                            className={`w-full text-left rounded border px-3 py-2 ${
                              isSelected
                                ? 'border-indigo-500 bg-indigo-950/40'
                                : 'border-gray-800 bg-gray-950/60 hover:bg-gray-800/60'
                            }`}
                          >
                            <div className="flex flex-wrap items-start justify-between gap-2">
                              <button
                                type="button"
                                className="min-w-0 flex-1 text-left"
                                onClick={() => setSelectedWidgetId(widget.id)}
                              >
                                <div className="text-sm text-gray-400">#{widget.id}</div>
                                <div className="text-base font-semibold">{widget.title}</div>
                                <div className="text-xs text-gray-400">
                                  {widget.partner}
                                  {widget.widget_type ? ` · ${widget.widget_type}` : ''}
                                </div>
                                <div className="text-xs text-gray-400">{widget.status}</div>
                              </button>
                              <div className="flex items-center gap-2">
                                {isSelected && (
                                  <span className="text-xs text-indigo-300">Selected</span>
                                )}
                                <button
                                  type="button"
                                  className="rounded bg-indigo-700 px-2 py-1 text-xs text-white"
                                  onClick={() => onEditWidget?.(widget.id)}
                                >
                                  Edit
                                </button>
                              </div>
                            </div>
                            {Array.isArray(widget.tags) && widget.tags.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {widget.tags.map((tag) => (
                                  <span
                                    key={tag}
                                    className="px-2 py-1 rounded-full bg-gray-800 text-xs text-gray-200"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <button
                        className="px-3 py-2 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                        onClick={addWidgetBlock}
                        disabled={!selectedWidget}
                      >
                        Add Inline Widget
                      </button>
                      {selectedWidget ? (
                        <div className="text-xs text-gray-400">
                          Selected: {selectedWidget.title || `Widget #${selectedWidget.id}`}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400">Select a widget to add it inline.</div>
                      )}
                    </div>
                  </div>
                )}

                {!sidebarCollapsed && rightTab === 'photos' && (
                  <div className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
                    <h3 className="text-lg font-semibold mb-3">Photo Search (Pixabay + Pexels)</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <input
                        className="flex-1 min-w-[200px] rounded bg-gray-800 border border-gray-700 px-3 py-2 text-white"
                        placeholder="Search both services (e.g. hoi an lanterns)"
                        value={photoSearchQuery}
                        onChange={(e) => setPhotoSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') runPhotoSearch();
                        }}
                      />
                      <button
                        type="button"
                        className="px-3 py-2 rounded bg-indigo-600 text-white disabled:bg-indigo-900 disabled:text-gray-400"
                        onClick={runPhotoSearch}
                        disabled={photoSearchLoading}
                      >
                        {photoSearchLoading ? 'Searching…' : 'Search'}
                      </button>
                    </div>
                    {photoSearchError && (
                      <div className="text-sm text-red-300 mb-3">{photoSearchError}</div>
                    )}
                    {photoSearchLoading && (
                      <div className="text-sm text-gray-400">Searching Pixabay and Pexels…</div>
                    )}
                    {!photoSearchLoading && (
                      <>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                          <div className="rounded border border-gray-800 bg-gray-950/70 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-semibold text-gray-200">Pixabay</div>
                            <div className="text-xs text-gray-500">{pixabayTotalHits.toLocaleString()} results</div>
                          </div>
                          {pixabayResults.length === 0 ? (
                            <div className="text-sm text-gray-500">No Pixabay images yet.</div>
                          ) : (
                            <div
                              className="space-y-3 max-h-[44vh] overflow-auto pr-1"
                              onScroll={(event) => {
                                const node = event.currentTarget;
                                if (node.scrollTop + node.clientHeight >= node.scrollHeight - 120) {
                                  loadMorePixabay();
                                }
                              }}
                            >
                              {pixabayResults.slice(0, pixabayVisibleCount).map((photo) => {
                                const isSelected =
                                  selectedRemotePhoto &&
                                  selectedRemotePhoto.provider === 'pixabay' &&
                                  String(selectedRemotePhoto.id) === String(photo.id);
                                return (
                                <button
                                  key={`pixabay-${photo.id}`}
                                  type="button"
                                  onClick={() => selectRemotePhoto('pixabay', photo)}
                                  className={`w-full text-left block rounded border bg-gray-950 overflow-hidden hover:border-indigo-500 ${
                                    isSelected ? 'border-indigo-500 ring-1 ring-indigo-500' : 'border-gray-800'
                                  }`}
                                  title={photo.tags || `Photo #${photo.id}`}
                                >
                                  <img
                                    src={resolvePhotoPreviewUrl(photo)}
                                    alt={photo.tags || `Pixabay ${photo.id}`}
                                    className="w-full h-40 object-cover bg-gray-900"
                                    loading="lazy"
                                    onError={(event) => handlePhotoPreviewError(event, photo)}
                                  />
                                  <div className="p-2">
                                    <div className="text-xs text-gray-300 line-clamp-2">{photo.tags || 'No tags'}</div>
                                    <div className="text-[11px] text-gray-500 mt-1">
                                      by {photo.user || 'unknown'} · {photo.imageWidth || 0}x{photo.imageHeight || 0}
                                    </div>
                                    {photo.pageUrl && (
                                      <a
                                        href={photo.pageUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[11px] text-indigo-300 hover:underline"
                                        onClick={(event) => event.stopPropagation()}
                                      >
                                        View source
                                      </a>
                                    )}
                                  </div>
                                </button>
                              );
                              })}
                              {(pixabayLoadingMore || pixabayHasMore) && (
                                <div className="text-xs text-gray-500 py-1">
                                  {pixabayLoadingMore ? `Loading ${PHOTO_RESULTS_PAGE_SIZE} more...` : 'Scroll for more'}
                                </div>
                              )}
                            </div>
                          )}
                          <div className="mt-2 text-[11px] text-gray-500">
                            Showing {Math.min(pixabayVisibleCount, pixabayResults.length)} of {pixabayTotalHits.toLocaleString()}
                          </div>
                        </div>

                        <div className="rounded border border-gray-800 bg-gray-950/70 p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="text-sm font-semibold text-gray-200">Pexels</div>
                            <div className="text-xs text-gray-500">{pexelsTotalHits.toLocaleString()} results</div>
                          </div>
                          {pexelsResults.length === 0 ? (
                            <div className="text-sm text-gray-500">No Pexels images yet.</div>
                          ) : (
                            <div
                              className="space-y-3 max-h-[44vh] overflow-auto pr-1"
                              onScroll={(event) => {
                                const node = event.currentTarget;
                                if (node.scrollTop + node.clientHeight >= node.scrollHeight - 120) {
                                  loadMorePexels();
                                }
                              }}
                            >
                              {pexelsResults.slice(0, pexelsVisibleCount).map((photo) => {
                                const isSelected =
                                  selectedRemotePhoto &&
                                  selectedRemotePhoto.provider === 'pexels' &&
                                  String(selectedRemotePhoto.id) === String(photo.id);
                                return (
                                <button
                                  key={`pexels-${photo.id}`}
                                  type="button"
                                  onClick={() => selectRemotePhoto('pexels', photo)}
                                  className={`w-full text-left block rounded border bg-gray-950 overflow-hidden hover:border-indigo-500 ${
                                    isSelected ? 'border-indigo-500 ring-1 ring-indigo-500' : 'border-gray-800'
                                  }`}
                                  title={photo.tags || `Photo #${photo.id}`}
                                >
                                  <img
                                    src={resolvePhotoPreviewUrl(photo)}
                                    alt={photo.tags || `Pexels ${photo.id}`}
                                    className="w-full h-40 object-cover bg-gray-900"
                                    loading="lazy"
                                    onError={(event) => handlePhotoPreviewError(event, photo)}
                                  />
                                  <div className="p-2">
                                    <div className="text-xs text-gray-300 line-clamp-2">{photo.tags || 'No tags'}</div>
                                    <div className="text-[11px] text-gray-500 mt-1">
                                      by {photo.user || 'unknown'} · {photo.imageWidth || 0}x{photo.imageHeight || 0}
                                    </div>
                                    {photo.pageUrl && (
                                      <a
                                        href={photo.pageUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-[11px] text-indigo-300 hover:underline"
                                        onClick={(event) => event.stopPropagation()}
                                      >
                                        View source
                                      </a>
                                    )}
                                  </div>
                                </button>
                              );
                              })}
                              {(pexelsLoadingMore || pexelsHasMore) && (
                                <div className="text-xs text-gray-500 py-1">
                                  {pexelsLoadingMore ? `Loading ${PHOTO_RESULTS_PAGE_SIZE} more...` : 'Scroll for more'}
                                </div>
                              )}
                            </div>
                          )}
                          <div className="mt-2 text-[11px] text-gray-500">
                            Showing {Math.min(pexelsVisibleCount, pexelsResults.length)} of {pexelsTotalHits.toLocaleString()}
                          </div>
                          </div>
                        </div>
                        <div className="mt-4 rounded border border-gray-800 bg-gray-950/70 p-3">
                          <div className="text-sm font-semibold text-gray-200 mb-2">Selected Photo Preview</div>
                          {!selectedRemotePhoto ? (
                            <div className="text-sm text-gray-500">Select one image from Pixabay or Pexels.</div>
                          ) : (
                            <div className="space-y-3">
                              <img
                                src={resolvePhotoPreviewUrl(selectedRemotePhoto)}
                                alt={selectedRemotePhoto.tags || 'Selected photo'}
                                className="w-full max-h-64 object-cover rounded border border-gray-800 bg-gray-900"
                                onError={(event) => handlePhotoPreviewError(event, selectedRemotePhoto)}
                              />
                              <div className="text-xs text-gray-400">
                                Source: {selectedRemotePhoto.provider} · {selectedRemotePhoto.user || 'unknown'}
                              </div>
                              <button
                                type="button"
                                className="px-3 py-2 rounded bg-emerald-600 text-white hover:bg-emerald-500 disabled:bg-emerald-900 disabled:text-gray-400"
                                onClick={importSelectedRemotePhoto}
                                disabled={!selectedRemotePhoto || importingRemotePhoto}
                              >
                                {importingRemotePhoto ? 'Importing…' : 'Add Selected Image'}
                              </button>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
